import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";

export const policyContent = [
  {
    number: 1,
    title: "Policy Hierarchy & Document Status",
    description: "Purpose, document architecture, ownership and governance lifecycle",
    content: (
      <div className="space-y-4">
        <Card>
          <CardHeader>
            <CardTitle>1.1. Purpose of the Group Investment Policy</CardTitle>
          </CardHeader>
          <CardContent>
            <p>
              The Group Investment Policy (GIP) fulfils two critical functions. First, it codifies the non-negotiable principles 
              that define our fiduciary posture toward clients whose assets sit within the Model Portfolio Service (MPS). Second, 
              it constructs a legal and procedural bridge between high-level Board directives and the day-to-day activities executed 
              by portfolio managers, traders, risk analysts and operations staff. In effect, the document translates the Board's 
              strategic intent into operational guardrails, thereby ensuring the Group's behaviour remains consistent, transparent 
              and auditable, even as markets, regulations and technology evolve.
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>1.2. Three-Tier Document Architecture</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="mb-4">
              CBAM employs a deliberately layered documentation structure that mirrors best practice across regulated financial institutions:
            </p>
            
            <div className="overflow-x-auto">
              <table className="min-w-full border border-border">
                <thead className="bg-muted">
                  <tr>
                    <th className="border border-border px-4 py-2">Level</th>
                    <th className="border border-border px-4 py-2">Document Type</th>
                    <th className="border border-border px-4 py-2">Responsibility</th>
                    <th className="border border-border px-4 py-2">Change Authority</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td className="border border-border px-4 py-2">1</td>
                    <td className="border border-border px-4 py-2">Group Investment Policy (this document)</td>
                    <td className="border border-border px-4 py-2">CBAM Board</td>
                    <td className="border border-border px-4 py-2">Board Investment Oversight Sub-Committee</td>
                  </tr>
                  <tr>
                    <td className="border border-border px-4 py-2">2</td>
                    <td className="border border-border px-4 py-2">MPS Strategy, Governance & Operations Manual</td>
                    <td className="border border-border px-4 py-2">Chief Investment Officer (CIO)</td>
                    <td className="border border-border px-4 py-2">Investment Committee (with Board noting for material changes)</td>
                  </tr>
                  <tr>
                    <td className="border border-border px-4 py-2">3</td>
                    <td className="border border-border px-4 py-2">Desk-level Standard Operating Procedures (SOPs)</td>
                    <td className="border border-border px-4 py-2">Functional Heads (PM, Trading, Risk, Ops)</td>
                    <td className="border border-border px-4 py-2">Functional Heads + Compliance sign-off</td>
                  </tr>
                </tbody>
              </table>
            </div>

            <p className="mt-4">
              Level 1 articulates "what must be true." It sets immutable purpose, investment philosophy, risk appetite and governance 
              mandates. Level 2 explains "how the Group will achieve those truths." It converts principles into practical policies, 
              e.g., asset allocation methodology, rebalancing logic, and defines quantitative limits. Level 3 documents "how each desk 
              will execute." These SOPs include order-management checklists, data-reconciliation runbooks, and incident-escalation scripts. 
              Wherever lower-level material appears to diverge from Level 1, the stricter requirement applies. This supremacy clause is 
              explicitly written into each Level 2 and Level 3 document, removing ambiguity in the event of conflict.
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>1.3. Ownership and Governance Lifecycle</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="mb-4">
              Although drafting accountability sits with the CIO, ownership remains with the Board. This distinction ensures strategic 
              control is never inadvertently delegated. The governance lifecycle is as follows:
            </p>
            
            <ul className="list-disc list-inside space-y-2 ml-4">
              <li>
                <strong>Drafting Phase:</strong> CIO convenes a drafting working group comprising senior representatives from Portfolio 
                Management, Risk, Compliance, and Operations.
              </li>
              <li>
                <strong>SMF Review:</strong> All Senior Managers Function holders (SMF 1 CEO, SMF 3 Executive Director, SMF 4 CRO, 
                SMF 27 COO) review the draft to confirm alignment with their Statements of Responsibility.
              </li>
              <li>
                <strong>Legal Vetting (if needed):</strong> General Counsel (or Compliance head) confirms compatibility with contractual 
                documents (IMAs, Terms & Conditions) and regulatory lexicon.
              </li>
              <li>
                <strong>Board Sub-Committee Approval:</strong> Investment Oversight Sub-Committee scrutinises, challenges and approves. 
                A unanimous vote is sought; a two-thirds majority suffices.
              </li>
              <li>
                <strong>Board Ratification:</strong> Final step for Level 1 documents; ratification recorded in Board minutes.
              </li>
              <li>
                <strong>Publication & Communication:</strong> Controlled distribution via the corporate intranet's Policy Library. 
                A policy-change summary email is circulated to all staff, and an adviser-facing bulletin is issued if client 
                communications are affected.
              </li>
              <li>
                <strong>Ongoing Monitoring:</strong> Compliance maintains a Policy Adherence Register tracking any waivers, breaches 
                or interim amendments. Breaches trigger escalation under Section 16.
              </li>
              <li>
                <strong>Scheduled Review:</strong> Full Board review at least annually, or earlier upon regulatory triggers (e.g., 
                FCA Dear CEO letters) or material risk events (e.g., VaR breaches).
              </li>
            </ul>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>1.4. Legal Status and Enforceability</CardTitle>
          </CardHeader>
          <CardContent>
            <p>
              This document forms part of the contractual architecture binding CBAM to its fiduciary obligations. While not a client 
              contract per se, it is referenced in adviser agreements and marketing collaterals as evidence of governance quality. 
              In the event of litigation or FCA thematic review, this document will serve as prima facie evidence that the firm had 
              a coherent, Board-approved policy framework. Non-compliance with the GIP is therefore both a regulatory breach and a 
              potential breach of fiduciary duty.
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>1.5. Change-Control Protocol</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="mb-4">To balance agility with control, changes are categorised:</p>
            
            <ul className="list-disc list-inside space-y-2 ml-4">
              <li>
                <strong>Material Changes:</strong> Any amendment that alters risk appetite, investment objectives, cost caps, or the 
                methodology for constructing the primary composite benchmarks. Requires full Board approval prior to implementation.
              </li>
              <li>
                <strong>Significant but Non-Material:</strong> Adjustments to numerical thresholds within existing bands (e.g., 
                tightening a tracking error budget from 2.5% to 2.0%). Requires Sub-Committee approval and post-hoc Board noting.
              </li>
              <li>
                <strong>Administrative:</strong> Typographic corrections, reference updates, or cross-link fixes. Approved by the CIO 
                and logged in the Document History table without Board escalation.
              </li>
            </ul>

            <p className="mt-4">
              All changes follow a version-control workflow in the firm's document management system, ensuring PDF snapshots are stored 
              immutably and previous versions remain accessible for at least seven years, satisfying internal record-keeping obligations.
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>1.6. Distribution and Accessibility</CardTitle>
          </CardHeader>
          <CardContent>
            <p>
              The latest signed-off PDF resides in the GDrive's Cape Berkshire Operations {'>'} Compliance {'>'} Policy Library {'>'} Level 1 folder, 
              with read-only permissions for all staff and read/write access restricted to the CIO's office, Compliance and Legal. 
              A HTML rendition is hosted on the secure Staff portal on the CBAM website for accessibility and for linking footnotes to 
              internal resources (e.g., RiskEngine.gsheets). External advisers receive a summarised two-page "Policy Highlights" sheet 
              covering investment philosophy, risk limits and cost caps.
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>1.7. Relationship to External Stakeholders</CardTitle>
          </CardHeader>
          <CardContent>
            <p>
              Rating agencies (e.g., ARC, Defaqto), external auditors, and platform partners may periodically request evidence of governance. 
              The GIP is the primary artefact furnished, subject to Non-Disclosure Agreement, during due diligence exercises.
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>1.8. Attestation of Compliance</CardTitle>
          </CardHeader>
          <CardContent>
            <p>
              Functional heads attest quarterly that their teams have operated in accordance with the GIP. Attestations feed into the 
              Chief Risk Officer's (CRO) semi-annual Risk Report, which is tabled at the Board. Failure to attest, or noted exceptions, 
              trigger Section 16 breach-escalation protocols.
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>1.9. Operational Resilience and Role Secondment</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="mb-4">
              The Group operates a lean staffing model, which necessitates a robust framework for ensuring business continuity in the 
              event of temporary or permanent vacancies in key positions. This policy codifies the process for seconding responsibilities 
              to other qualified staff members to maintain operational integrity and uphold our fiduciary duties without interruption. 
              All secondments are temporary measures pending the recruitment of a permanent replacement. Any such arrangement must be 
              formally documented, noting the duration and scope of responsibilities, and approved by the Board or the relevant Senior 
              Manager. The Conflict of Interest standards outlined in Section 15 must be strictly observed, with additional oversight 
              implemented where necessary.
            </p>

            <p className="mb-4">The following coverage plan outlines the designated primary and secondary personnel for key investment and governance roles:</p>

            <div className="space-y-4 ml-4">
              <div>
                <h3 className="font-bold mb-2">Chief Investment Officer (CIO):</h3>
                <p className="mb-2">In the event of a vacancy, the CIO's responsibilities will be bifurcated to ensure both strategic oversight and operational continuity, while managing conflicts:</p>
                <ul className="list-disc list-inside space-y-1 ml-4">
                  <li><strong>Primary Strategic Cover:</strong> The Chief Executive Officer (CEO) will assume ultimate responsibility for strategic investment decisions and chair the Investment Committee (IC).</li>
                  <li><strong>Primary Operational Cover:</strong> The Senior Portfolio Manager (SPM) will be responsible for the day-to-day execution of portfolio management, preparation of IC materials, and implementation of TAA decisions.</li>
                  <li><strong>Mandatory Oversight:</strong> During this period, the Chief Risk Officer (CRO) will provide enhanced, documented oversight of all IC decisions and portfolio risk metrics, reporting directly to the Board on this matter.</li>
                </ul>
              </div>

              <div>
                <h3 className="font-bold mb-2">Senior Portfolio Manager (SPM):</h3>
                <ul className="list-disc list-inside space-y-1 ml-4">
                  <li><strong>Primary Cover:</strong> The Chief Investment Officer (CIO) will assume direct control of day-to-day portfolio management tasks, including trade execution and monitoring.</li>
                  <li><strong>Supporting Role:</strong> The Investment Analyst (IA) will be delegated increased responsibility for preparing performance attribution, risk reports, and initial trade analysis for the CIO's review.</li>
                </ul>
              </div>

              <div>
                <h3 className="font-bold mb-2">Chief Risk Officer (CRO):</h3>
                <p className="mb-2">The independence of the risk function is paramount.</p>
                <ul className="list-disc list-inside space-y-1 ml-4">
                  <li><strong>Primary Cover:</strong> The Head of Compliance (if a separate post) may take on the role, provided they have no conflicting responsibilities.</li>
                  <li><strong>Prohibition:</strong> This role cannot be covered by the CEO, CIO, or SPM due to the fundamental conflict between the functions of generating returns and overseeing risk.</li>
                </ul>
              </div>

              <div>
                <h3 className="font-bold mb-2">Investment Analyst (IA):</h3>
                <ul className="list-disc list-inside space-y-1 ml-4">
                  <li><strong>Primary Cover:</strong> The Senior Portfolio Manager (SPM) will absorb the core analytical and data management duties of the IA.</li>
                  <li><strong>Contingency:</strong> The IC will be formally notified of the vacancy. Given the increased workload on the SPM, the IC may resolve to temporarily reduce the frequency or magnitude of tactical tilts to ensure the core SAA implementation remains robust.</li>
                </ul>
              </div>

              <div>
                <h3 className="font-bold mb-2">Compliance Officer:</h3>
                <ul className="list-disc list-inside space-y-1 ml-4">
                  <li><strong>Primary Cover:</strong> The Chief Risk Officer (CRO) will assume direct responsibility for the compliance function.</li>
                  <li><strong>Secondary Cover:</strong> In the absence of a CRO, a senior manager from a non-investment function (e.g., Chief Operating Officer) may be delegated specific compliance tasks, operating under the direct guidance and supervision of the CEO and the Board.</li>
                </ul>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    ),
  },

  {
    number: 2,
    title: "Fiduciary Framework & Regulatory Context",
    description: "Core fiduciary duties, regulatory obligations and supervisory expectations",
    content: (
      <div className="space-y-4">
        <Card>
          <CardHeader>
            <CardTitle>2.1. Core Fiduciary Duties</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="mb-4">
              UK case law, common law principles and FCA conduct rules converge on two overarching fiduciary duties: loyalty and prudence.
            </p>
            <ul className="list-disc list-inside space-y-2 ml-4">
              <li>
                <strong>Loyalty:</strong> We must place client interests ahead of the firm's commercial interests. This manifests in conflict 
                management (Section 15), cost caps (Section 13) and the exclusion of products unsuited to the target market (Section 4).
              </li>
              <li>
                <strong>Prudence:</strong> We must exercise the care, skill and diligence that a reasonably prudent investment professional 
                would under comparable circumstances. Prudence underpins our risk budgets (Section 11) and stress-testing (Section 11.5).
              </li>
            </ul>
            <p className="mt-4">
              Failure in either duty exposes the firm to FCA enforcement, civil litigation and reputational damage. Directors should note 
              that fiduciary liability is personal: delegation to committees does not absolve oversight responsibilities.
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>2.2. UK Regulatory Framework</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="mb-4">
              The MPS operates within a multi-layered FCA rulebook. The following table summarises the most pertinent sourcebooks and how 
              this GIP responds.
            </p>
            
            <div className="overflow-x-auto">
              <table className="min-w-full border border-border">
                <thead className="bg-muted">
                  <tr>
                    <th className="border border-border px-4 py-2">FCA Rulebook / PS</th>
                    <th className="border border-border px-4 py-2">Focus</th>
                    <th className="border border-border px-4 py-2">GIP Clause(s)</th>
                    <th className="border border-border px-4 py-2">Evidence Artifact</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td className="border border-border px-4 py-2">PRIN 12 Consumer Duty</td>
                    <td className="border border-border px-4 py-2">Fair value, good outcomes</td>
                    <td className="border border-border px-4 py-2">Section 13 (Cost Caps); Section 12 (Performance)</td>
                    <td className="border border-border px-4 py-2">Quarterly VfM Dashboard</td>
                  </tr>
                  <tr>
                    <td className="border border-border px-4 py-2">PROD 4.2</td>
                    <td className="border border-border px-4 py-2">Target market & ongoing review</td>
                    <td className="border border-border px-4 py-2">Section 4.2 (Risk Profile Mapping)</td>
                    <td className="border border-border px-4 py-2">Target Market Matrix</td>
                  </tr>
                  <tr>
                    <td className="border border-border px-4 py-2">MIFIDPRU</td>
                    <td className="border border-border px-4 py-2">Prudential capital & liquidity</td>
                    <td className="border border-border px-4 py-2">Section 11.6 (Capital Adequacy)</td>
                    <td className="border border-border px-4 py-2">ICAAP Report</td>
                  </tr>
                  <tr>
                    <td className="border border-border px-4 py-2">COBS 11.2A</td>
                    <td className="border border-border px-4 py-2">Best execution</td>
                    <td className="border border-border px-4 py-2">Section 13.2 (TCA)</td>
                    <td className="border border-border px-4 py-2">TCA Reports</td>
                  </tr>
                  <tr>
                    <td className="border border-border px-4 py-2">PS21/3 (Operational Resilience)</td>
                    <td className="border border-border px-4 py-2">Important business services</td>
                    <td className="border border-border px-4 py-2">Section 16 (Breach Protocol)</td>
                    <td className="border border-border px-4 py-2">Impact Tolerance Documents</td>
                  </tr>
                  <tr>
                    <td className="border border-border px-4 py-2">PS23/13 (Sustainability Disclosure)</td>
                    <td className="border border-border px-4 py-2">SDR labelling & disclosures</td>
                    <td className="border border-border px-4 py-2">Section 9 (RI Vision)</td>
                    <td className="border border-border px-4 py-2">ESG Factsheet Addendum</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>2.3. Cross-Border & Extra-Territorial Considerations</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="mb-4">
              Although our client base is UK-centric, assets may be custodied on overseas platforms and invested in UCITS domiciled in 
              the EU or ETFs listed in the US. Accordingly, we monitor the following regimes:
            </p>
            <ul className="list-disc list-inside space-y-2 ml-4">
              <li>
                <strong>EU SFDR & Taxonomy:</strong> Sustainable portfolios must avoid greenwashing risk; ESG data mapping aligns with 
                Article 8 labelling parameters.
              </li>
              <li>
                <strong>EMIR/UK EMIR:</strong> Relevance informs collateral eligibility for permitted derivatives (Section 7).
              </li>
            </ul>
            <p className="mt-4">
              A triage matrix maintained by Legal & Compliance tracks regulatory changes in these jurisdictions and flags amendments to 
              the GIP if material.
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>2.4. Supervisory Horizon Scan (2025-27)</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="mb-4">
              The Regulatory Affairs team issues a quarterly "Horizon Scan" memo. Key items on the radar include:
            </p>
            <ul className="list-disc list-inside space-y-2 ml-4">
              <li>
                <strong>FCA SDR Phase 2:</strong> Mandatory consumer-facing sustainability labels, originally mandated from July 2026 but 
                currently indefinitely postponed. Our planned Sustainable MPS range will require additional disclosures.
              </li>
              <li>
                <strong>Basel III Endgame:</strong> Potential indirect impact on counterparty banks' balance sheets, affecting repo rates 
                and liquidity terms.
              </li>
              <li>
                <strong>UK TPT (Transition Plan Taskforce):</strong> Draft guidance implies more granular climate-transition reporting by 2027.
              </li>
              <li>
                <strong>DORA (EU Digital Operational Resilience Act):</strong> Although primarily EU, our Irish platform wrapper may capture 
                intra-group ICT service provision.
              </li>
            </ul>
            <p className="mt-4">
              These emerging rules are colour-coded (Red, Amber, Green) in the Horizon Scan and mapped to a "change owner." If a rule is 
              graded Amber or Red, a Fast-Track GIP amendment (Section 18) is triggered.
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>2.5. Regulatory Reporting & Interaction Calendar</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="min-w-full border border-border">
                <thead className="bg-muted">
                  <tr>
                    <th className="border border-border px-4 py-2">Report / Interaction</th>
                    <th className="border border-border px-4 py-2">Regulatory Body</th>
                    <th className="border border-border px-4 py-2">Frequency</th>
                    <th className="border border-border px-4 py-2">Owner</th>
                    <th className="border border-border px-4 py-2">GIP Reference</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td className="border border-border px-4 py-2">MIFIDPRU IFPR Return</td>
                    <td className="border border-border px-4 py-2">FCA</td>
                    <td className="border border-border px-4 py-2">Quarterly</td>
                    <td className="border border-border px-4 py-2">Finance</td>
                    <td className="border border-border px-4 py-2">Section 11.6</td>
                  </tr>
                  <tr>
                    <td className="border border-border px-4 py-2">REP005 Client Money</td>
                    <td className="border border-border px-4 py-2">FCA</td>
                    <td className="border border-border px-4 py-2">Annual</td>
                    <td className="border border-border px-4 py-2">Operations</td>
                    <td className="border border-border px-4 py-2">BCP Manual</td>
                  </tr>
                  <tr>
                    <td className="border border-border px-4 py-2">Product Review Report</td>
                    <td className="border border-border px-4 py-2">FCA / Internal</td>
                    <td className="border border-border px-4 py-2">Annual</td>
                    <td className="border border-border px-4 py-2">CIO</td>
                    <td className="border border-border px-4 py-2">Section 18</td>
                  </tr>
                  <tr>
                    <td className="border border-border px-4 py-2">TCFD Disclosures</td>
                    <td className="border border-border px-4 py-2">FCA / Market</td>
                    <td className="border border-border px-4 py-2">Annual</td>
                    <td className="border border-border px-4 py-2">ESG Lead</td>
                    <td className="border border-border px-4 py-2">Section 9</td>
                  </tr>
                  <tr>
                    <td className="border border-border px-4 py-2">Board FCA Annual CEO Section 13 Attestation (PRIN 12)</td>
                    <td className="border border-border px-4 py-2">FCA</td>
                    <td className="border border-border px-4 py-2">Annual</td>
                    <td className="border border-border px-4 py-2">CEO</td>
                    <td className="border border-border px-4 py-2">Section 13</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <p className="mt-4">
              All regulatory submissions undergo a "four-eyes" review (preparer + reviewer) and are archived in immutable storage.
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>2.6. Compliance Oversight Model</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="mb-4">Compliance operates a three-lines-of-defence model:</p>
            <ul className="list-disc list-inside space-y-2 ml-4">
              <li>
                <strong>Line 1:</strong> Portfolio Management & Trading own the risks, detecting breaches in real-time via rules embedded 
                in the RiskEngine.gsheets workbook.
              </li>
              <li>
                <strong>Line 2:</strong> Compliance & Risk perform daily surveillance, monthly thematic reviews and maintain the Policy 
                Adherence Register.
              </li>
              <li>
                <strong>Line 3:</strong> Internal Audit provides independent assurance, typically on a two-year rolling cycle, with ad-hoc 
                audits triggered by Board request.
              </li>
            </ul>
            <p className="mt-4">
              Compliance also chairs a Monthly Regulatory Change Forum where new consultation papers are dissected and action owners assigned.
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>2.7. Linkage to the Senior Managers & Certification Regime</CardTitle>
          </CardHeader>
          <CardContent>
            <p>
              The GIP forms part of the documented Statement of Responsibilities for SMF 3 (Executive Director) and SMF 4 (Chief Risk 
              Officer). Breach of the GIP may therefore constitute a breach of Conduct Rules and, in serious cases, lead to regulatory 
              censure or enforcement against individuals.
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>2.8. Legal Opinion on Enforceability</CardTitle>
          </CardHeader>
          <CardContent>
            <p>
              Where required, AWM will consult with external counsel in order to obtain a comfort letter stating that the GIP is consistent 
              with current UK regulatory obligations and, if adhered to, provides a robust defence against foreseeable claims of misselling 
              or negligent portfolio management.
            </p>
          </CardContent>
        </Card>
      </div>
    ),
  },

  {
    number: 3,
    title: "Investment Philosophy",
    description: "Core propositions, behavioural discipline and continuous improvement",
    content: (
      <div className="space-y-4">
        <div className="mb-6">
          <p>
            Our investment philosophy is the north star that keeps every investment decision, strategic or tactical, aligned with our 
            fiduciary duty. It is informed by academic research, sharpened by behavioural-finance insights, and stress-tested against 
            real-world frictions such as transaction costs and regulation. This philosophy has undergone a significant evolution. We 
            have moved from a model predicated on internal macroeconomic forecasting to one grounded in the collective wisdom of the 
            market's leading multi-asset practitioners. This shift is a deliberate strategic decision to enhance the robustness and 
            reliability of client outcomes, aligning our value proposition with governance and disciplined implementation rather than 
            speculative prediction.
          </p>
        </div>

        <Card>
          <CardHeader>
            <CardTitle>3.1. Proposition 1: Asset Allocation Dominates, and Collective Wisdom Determines It Best</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="mb-4">
              Decades of empirical research, notably the work of Brinson, Hood, and Beebower (1986), have established that the strategic 
              mix of asset classes is the primary determinant of portfolio return variability over time. We fully subscribe to this 
              principle. However, our philosophy diverges from traditional approaches on the question of how to determine the optimal 
              asset mix. We believe that for retail clients, the most effective and reliable Strategic Asset Allocation (SAA) is not 
              derived from point-in-time, and often fallible, internal capital market assumptions. Instead, we harness the collective, 
              time-tested wisdom of a carefully selected peer group of leading UK multi-asset fund managers.
            </p>
            <p>
              Our core belief is that a transparent, rules-based, and equally-weighted composite of these managers' portfolios (our AWM 
              Composite Benchmark) provides the most robust and durable SAA for each client risk profile. This approach deliberately 
              mitigates the key-person risk and potential for forecasting error inherent in single-manager models. Our primary value is 
              added not through macro-economic forecasting, but through the disciplined governance of this benchmark, the rigorous due 
              diligence on its constituents, and the efficient implementation of portfolios that align with it. This philosophy directly 
              supports our obligations under the FCA's Consumer Duty by anchoring our strategy in a diversified, market-tested consensus, 
              thereby reducing the risk of poor outcomes stemming from idiosyncratic or inaccurate internal views.
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>3.2. Proposition 2: Costs Compound Negatively</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="mb-4">
              A pound not spent is a pound that continues to compound for clients. Our core–satellite architecture operationalises this 
              idea. Low-cost index funds provide broad beta exposure in line with our benchmark-derived SAA, while concentrated active 
              strategies may be considered for demonstrably inefficient markets where there is a high probability of generating alpha net 
              of fees.
            </p>
            <p className="mb-4">Oversight mechanisms translate the philosophy into practice:</p>
            <ul className="list-disc list-inside space-y-2 ml-4">
              <li>
                First, OCF caps. For the Balanced profile the blended fund Ongoing Charges Figure must not exceed 45 basis points. The 
                COO's Business Performance team monitors fund OCFs monthly; any drift above caps automatically generates an alert in the 
                Policy Adherence Register.
              </li>
              <li>
                Second, real-time transaction-cost surveillance. Every trade is captured by an in-house Google Sheets TCA toolkit built 
                on post-trade data. Implementation shortfall is benchmarked against arrival price and routed to an execution-quality 
                dashboard that portfolio managers review after each portfolio trade.
              </li>
              <li>
                Third, an annual Value-for-Money (VfM) review. Each January the CIO presents to the Board a VfM pack comparing net returns, 
                risk, service metrics and cost against the ARC MPS Indices. Findings feed into the IC's annual benchmark review, ensuring 
                that cost is treated as an equal partner to return and risk.
              </li>
            </ul>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>3.3. Proposition 3: Responsible Investment Enhances Risk-Adjusted Returns</CardTitle>
          </CardHeader>
          <CardContent>
            <p>
              We treat Environmental, Social, and Governance (ESG) factors as financially material variables, not moral luxuries. While 
              our core MPS range is not explicitly ESG-labelled at present, our investment process acknowledges that sustainable business 
              practices can lower a company's cost of capital and reduce downside risk. This belief manifests primarily through our fund 
              selection process, where a manager's ability to integrate ESG risks and opportunities is a key due diligence consideration. 
              As detailed in Section 9, our strategic aim is to launch a parallel, ESG-labelled portfolio suite. This future range will 
              embed more explicit ESG criteria, including exclusion screens and integration scores, directly into its construction, 
              formalising this philosophical proposition into a distinct product offering.
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>3.4. Proposition 4: Behavioural Discipline Safeguards Process</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="mb-4">
              Even professional investors are prey to cognitive biases such as overconfidence, herding, and narrative fallacy. To minimise 
              such errors, our governance framework embeds three behavioural circuit-breakers:
            </p>
            <ul className="list-disc list-inside space-y-2 ml-4">
              <li>
                <strong>Premortems:</strong> Before any significant tactical allocation change (defined as a tilt expected to contribute 
                more than 50% of the portfolio's tracking error budget), the Investment Committee (IC) imagines that the decision proves 
                disastrous within 12 months and back-casts reasons for failure. Likely culprits (over-reliance on a single macro indicator, 
                chasing performance) are documented, and mitigations are designed into the implementation plan.
              </li>
              <li>
                <strong>Red-Team Challenge:</strong> Every major TAA proposal is assigned a 'red-team' analyst mandated to construct the 
                most persuasive counter-argument. This adversarial process ensures that groupthink is exposed in a safe, structured 
                environment. Meeting minutes reflect both sides of the debate, satisfying regulatory expectations for robust challenge.
              </li>
              <li>
                <strong>Decision Diary:</strong> The diary logs data inputs, dissenting opinions, expected time horizon and exit criteria 
                for each tactical trade. Compliance audits the diary quarterly; incomplete entries are flagged to the CIO and may affect 
                PM remuneration.
              </li>
            </ul>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>3.5. Connecting Philosophy to Policy</CardTitle>
          </CardHeader>
          <CardContent>
            <p>
              Philosophy shapes policy in tangible ways. Because the benchmark-derived SAA is paramount, Board Approval is required for 
              any material change to the benchmark's construction methodology (Section 5.3). Cost discipline translates into numeric OCF 
              caps and transaction-cost budgets (Section 13). ESG beliefs manifest in our forward-looking plan for a dedicated sustainable 
              range (Section 9). Behavioural discipline is codified in the IC charter (Section 14), which mandates premortems and red-team 
              reviews for significant decisions.
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>3.6. Measurement and Continuous Improvement</CardTitle>
          </CardHeader>
          <CardContent>
            <p>
              Four Key Philosophy Indicators (KPIs) gauge whether we live up to our ideals. The contribution of the SAA (as represented 
              by the benchmark's return) to total return must remain the dominant driver. The tracking error relative to the benchmark is 
              closely monitored to ensure tactical bets do not overwhelm the strategic core. Cost efficiency is measured against caps. 
              Finally, Decision Diary completion is monitored to ensure behavioural safeguards remain active; a drop below 100% completeness 
              triggers compliance training.
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>3.7. External Validation and Periodic Audit Review</CardTitle>
          </CardHeader>
          <CardContent>
            <p>
              Every three years, the CIO commissions an External Philosophy Audit. An independent consultant benchmarks our framework 
              against peers. The next audit is scheduled for Q3 2028 and will coincide with the Strategy Manual's three-year refresh. 
              This external challenge ensures our philosophy does not become insular and remains aligned with industry best practice and 
              evolving regulatory expectations.
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>3.8. Future-Proofing the Philosophy</CardTitle>
          </CardHeader>
          <CardContent>
            <p>
              Financial markets and academic understanding evolve. Should compelling empirical evidence emerge that challenges any of 
              our four propositions (for instance, a structural breakdown in the diversification benefits offered by our benchmark's 
              consensus allocation) this section will be the first to change. The CIO can propose amendments outside the three-year cycle 
              if such evidence arises, triggering the change-control protocol in Section 18.
            </p>
          </CardContent>
        </Card>
      </div>
    ),
  },

  {
    number: 4,
    title: "Statement of Investment Objectives",
    description: "Concrete, measurable targets that drive portfolio design and oversight",
    content: (
      <div className="space-y-4">
        <div className="mb-6">
          <p>
            Our investment objectives translate the philosophical principles set out in Section 3 into concrete, measurable targets 
            that drive portfolio design, implementation, and oversight. The objectives are deliberately layered. At the core sits a 
            primary objective to preserve and grow purchasing power against UK inflation. This objective is pursued through a 
            disciplined alignment with a series of proprietary composite benchmarks, which serve as the investable expression of each 
            portfolio's intended risk and return profile. Around this sit risk budgets that safeguard capital and keep outcomes 
            consistent with each model's profile, primarily by managing deviation from these core benchmarks. Finally, we maintain 
            secondary commitments to cost discipline and liquidity resilience that ensure the service remains responsive to adviser 
            expectations and evolving regulation.
          </p>
        </div>

        <Card>
          <CardHeader>
            <CardTitle>4.1. Primary Objective: Delivering Inflation-Plus Returns and Income</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="mb-4">
              The primary objective of each model portfolio is to deliver a rate of return that exceeds UK Consumer Price Inflation 
              (CPI) over any rolling five-year period. For the Distribution suite, this is complemented by a primary objective to 
              deliver a specific target yield.
            </p>

            <div className="overflow-x-auto mb-4">
              <table className="min-w-full border border-border">
                <thead className="bg-muted">
                  <tr>
                    <th className="border border-border px-4 py-2">Portfolio Name</th>
                    <th className="border border-border px-4 py-2">Active Performance Benchmark</th>
                    <th className="border border-border px-4 py-2">Primary Performance Benchmark</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td className="border border-border px-4 py-2">1 (Defensive)</td>
                    <td className="border border-border px-4 py-2">AWM Composite</td>
                    <td className="border border-border px-4 py-2">CPI + 1%</td>
                  </tr>
                  <tr>
                    <td className="border border-border px-4 py-2">2 (Cautious)</td>
                    <td className="border border-border px-4 py-2">AWM Composite</td>
                    <td className="border border-border px-4 py-2">CPI + 2%</td>
                  </tr>
                  <tr>
                    <td className="border border-border px-4 py-2">3 (Balanced)</td>
                    <td className="border border-border px-4 py-2">AWM Composite</td>
                    <td className="border border-border px-4 py-2">CPI + 3%</td>
                  </tr>
                  <tr>
                    <td className="border border-border px-4 py-2">4 (Capital Growth)</td>
                    <td className="border border-border px-4 py-2">AWM Composite</td>
                    <td className="border border-border px-4 py-2">CPI + 4%</td>
                  </tr>
                  <tr>
                    <td className="border border-border px-4 py-2">5 (Aggressive)</td>
                    <td className="border border-border px-4 py-2">AWM Composite</td>
                    <td className="border border-border px-4 py-2">CPI + 5%</td>
                  </tr>
                </tbody>
              </table>
            </div>

            <div className="overflow-x-auto mb-4">
              <table className="min-w-full border border-border">
                <thead className="bg-muted">
                  <tr>
                    <th className="border border-border px-4 py-2">Portfolio Name</th>
                    <th className="border border-border px-4 py-2">Target Yield</th>
                    <th className="border border-border px-4 py-2">Primary Performance Benchmark</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td className="border border-border px-4 py-2">CBAM Dist Low</td>
                    <td className="border border-border px-4 py-2">4%+</td>
                    <td className="border border-border px-4 py-2">CPI + 1%</td>
                  </tr>
                  <tr>
                    <td className="border border-border px-4 py-2">CBAM Dist Medium</td>
                    <td className="border border-border px-4 py-2">4%+</td>
                    <td className="border border-border px-4 py-2">CPI + 2%</td>
                  </tr>
                  <tr>
                    <td className="border border-border px-4 py-2">CBAM Dist High</td>
                    <td className="border border-border px-4 py-2">4%+</td>
                    <td className="border border-border px-4 py-2">CPI + 3%</td>
                  </tr>
                </tbody>
              </table>
            </div>

            <p className="font-semibold mb-2">Table 4.1b: Objectives for the Money Market (MM) Suite</p>
            <div className="overflow-x-auto">
              <table className="min-w-full border border-border">
                <thead className="bg-muted">
                  <tr>
                    <th className="border border-border px-4 py-2">Portfolio Name</th>
                    <th className="border border-border px-4 py-2">Target Yield</th>
                    <th className="border border-border px-4 py-2">Primary Performance Benchmark</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td className="border border-border px-4 py-2">CBAM MM</td>
                    <td className="border border-border px-4 py-2">SONIA</td>
                    <td className="border border-border px-4 py-2">SONIA</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>4.2. Clarifying the Target Market and Suitability Framework</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="mb-4">
              To ensure that each objective is delivered to the right investor cohort, we map our five risk profiles to the adviser 
              community's common Attitude-to-Risk (ATR) scoring bands. This mapping is critical for satisfying the FCA's PROD rules 
              on product design and target market identification.
            </p>
            <ul className="list-disc list-inside space-y-2 ml-4">
              <li>
                Scores of 1 gravitate toward the Defensive (1) model. Such clients typically possess a low willingness to tolerate 
                market volatility. Recommended investment horizon: a minimum of three years.
              </li>
              <li>
                Scores of 2 funnel into the Cautious (2) model. Investors here seek a measured balance between growth and capital 
                stability, often planning for goals five or more years away.
              </li>
              <li>
                Scores of 3 align naturally with the Balanced (3) model, the most popular risk band. These clients accept short-term 
                drawdowns in exchange for meaningful growth and have horizons exceeding five years.
              </li>
              <li>
                Scores of 4 are matched to the Growth (4) model. These investors are usually building wealth for goals seven years or 
                more in the future and possess the psychological resilience to stay invested through equity bear markets.
              </li>
              <li>
                Scores of 5 point toward the Aggressive (5) model, appropriate for long-term pots where investors understand and accept 
                potential peak-to-trough drawdowns north of 20%.
              </li>
            </ul>
            <p className="mt-4">
              Capacity-for-loss considerations add a second dimension. Advisers are reminded, in the Adviser Implementation Guide, to 
              consider capacity in tandem with ATR before selecting a model.
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>4.3. Secondary Objectives: Cost, ESG and Liquidity</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="mb-4">
              In addition to the primary objectives, we articulate three secondary objectives that meet both regulatory expectations 
              under the FCA Consumer Duty and rising adviser demands for demonstrable value.
            </p>
            <ul className="list-disc list-inside space-y-2 ml-4">
              <li>
                <strong>Cost Efficiency:</strong> We run an explicit Total Cost of Ownership (TCO) cap for each risk profile covering 
                the blended Ongoing Charges Figure (OCF), our discretionary management fee, and measured trading costs. The Investment 
                Committee sets and reviews the caps annually. For 2025, the Balanced profile cap is 1.00%. Clean share classes are 
                mandatory.
              </li>
              <li>
                <strong>ESG Leadership:</strong> The core MPS range is not currently ESG-labelled, and no ESG KPI applies in 2025. Our 
                strategic aim is to launch a parallel ESG-labelled suite in 2026. At that point, we will add ESG-specific objectives 
                for that suite. Until then, stewardship remains with underlying managers and we avoid ESG claims for the core MPS.
              </li>
              <li>
                <strong>Liquidity Resilience:</strong> We operate to an internal target that portfolio liquidity should be sufficient, 
                under our defined stress assumptions, to liquidate at least 95% of Net Asset Value within five trading days. This is 
                monitored via a Liquidity Coverage Ratio (LCR) calculated in our RiskEngine.gsheets workbook.
              </li>
            </ul>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>4.4. Embedding Objectives in Operational Process</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="mb-4">
              Setting objectives is only meaningful if they drive day-to-day decisions. Therefore, each objective is mapped to a 
              specific process owner and embedded in formal MI reporting lines.
            </p>
            <ul className="list-disc list-inside space-y-2 ml-4">
              <li>
                The CPI-plus targets and benchmark-relative returns feed directly into the Performance Early Warning System (PEWS) 
                described in Section 12. Significant underperformance against either the absolute or relative target for two consecutive 
                quarters escalates to the IC.
              </li>
              <li>
                Cost efficiency metrics populate the quarterly Value-for-Money Dashboard, tabled at both IC and Board meetings.
              </li>
              <li>
                Liquidity KPIs are incorporated into the Risk MI Pack; deviations feed breach escalation under Section 16.
              </li>
            </ul>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>4.5. Continuous Suitability Feedback Loop</CardTitle>
          </CardHeader>
          <CardContent>
            <p>
              Twice a year, we review a sample of recent advised cases to confirm that inflows align with intended risk bands. The 
              review uses adviser-submitted ATR scores, chosen model profiles, and basic case notes. Any systematic mismatch is raised 
              with the relevant distributor and, if necessary, discussed at the Investment Committee with options to clarify 
              communications or adjust the mapping guidance. This process is managed in Google Sheets and Asana for traceability.
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>4.6. Board Accountability and KPI Dashboard</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="mb-4">
              The Board receives a concise Investment Objectives Dashboard each quarter. Traffic-light indicators show:
            </p>
            <ul className="list-disc list-inside space-y-2 ml-4">
              <li>Rolling five-year performance versus the CPI+ objective and the primary composite benchmark for each profile.</li>
              <li>TCO versus cap for each profile, with commentary on any remediation.</li>
              <li>Liquidity Coverage Ratio status.</li>
              <li>ESG readiness progress for the planned 2026 ESG suite.</li>
            </ul>
            <p className="mt-4">
              A red flag in any metric triggers an automatic agenda item for the next Board meeting, ensuring that high-level oversight 
              remains sharp and proactive.
            </p>
          </CardContent>
        </Card>
      </div>
    ),
  },

  {
    number: 5,
    title: "The Benchmark-Driven Asset Allocation Policy",
    description: "Repeatable, data-driven process for determining portfolio asset mix",
    content: (
      <div className="space-y-4">
        <div className="mb-6">
          <p>
            This section translates the investment philosophy of Section 3 into a repeatable, data-driven process for determining 
            each portfolio's neutral asset mix. It represents a fundamental departure from traditional SAA processes that rely on 
            internal capital market forecasting. Our SAA is not created; it is derived. The policy provides the how, moving from the 
            belief in collective wisdom to an executable, governable, and auditable set of portfolio weightings.
          </p>
        </div>

        <Card>
          <CardHeader>
            <CardTitle>5.1. The AWM Composite Benchmark: Construction and Methodology</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="mb-4">
              The AWM Composite Benchmark is the cornerstone of our investment process. It is a proprietary, rules-based composite 
              portfolio created and maintained within the FE Analytics platform. Its purpose is to serve as a stable, diversified, and 
              representative proxy for the consensus asset allocation of leading multi-asset managers for a given risk profile.
            </p>
            <p className="mb-2">The key principles of construction are:</p>
            <ul className="list-disc list-inside space-y-2 ml-4">
              <li>
                <strong>Risk Profile Alignment:</strong> The first and most critical criterion is the availability of a consistent range 
                of five risk-profiled funds: Defensive, Cautious, Balanced, Growth, and Aggressive. This ensures a like-for-like 
                comparison and allows for the creation of five distinct benchmarks that map directly to our five model portfolios.
              </li>
              <li>
                <strong>Representation and Diversification:</strong> To avoid concentration and representation bias, each composite 
                benchmark must include a minimum of five distinct fund ranges from different fund houses. This ensures the resulting SAA 
                is a true consensus and not skewed by the views of a single manager.
              </li>
              <li>
                <strong>Weighting Scheme:</strong> The constituent fund ranges within each benchmark are equally weighted. For example, 
                in the Balanced benchmark, if there are five approved fund houses, the Balanced fund from each house will receive a 20% 
                weighting. This simple, transparent methodology prevents market-cap or performance-chasing biases from influencing the 
                benchmark's composition.
              </li>
              <li>
                <strong>Due Diligence Criteria:</strong> Fund houses are only eligible for inclusion if they meet several qualitative 
                and quantitative criteria:
                <ul className="list-disc list-inside space-y-1 ml-6 mt-2">
                  <li><strong>Transparency:</strong> They must provide adequate documentation, including up-to-date fact sheets and full 
                  holdings disclosure, allowing for accurate SAA calculation.</li>
                  <li><strong>Reputation:</strong> The firms must be well-known, respectable, and reputable within the UK investment 
                  industry.</li>
                  <li><strong>Cost:</strong> While not the primary driver, the Annual Management Charges (AMC) of the underlying funds 
                  are considered to ensure the benchmark reflects reasonably priced solutions available to clients.</li>
                </ul>
              </li>
            </ul>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>5.2. Approved Fund Houses</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="mb-4">
              The Investment Committee is responsible for approving and maintaining the list of fund houses eligible for inclusion in 
              the composite benchmarks. The list is subject to an annual review as part of the benchmark governance cycle. The current 
              approved list distinguishes between active and passive strategies, allowing for the construction of distinct benchmark 
              variants if required for analytical purposes.
            </p>
            <div className="overflow-x-auto">
              <table className="min-w-full border border-border">
                <thead className="bg-muted">
                  <tr>
                    <th className="border border-border px-4 py-2">Active Fund Houses</th>
                    <th className="border border-border px-4 py-2">Passive Fund Houses</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td className="border border-border px-4 py-2">Architas Multi-Manager Blended</td>
                    <td className="border border-border px-4 py-2">Aviva Investments Multi-Asset</td>
                  </tr>
                  <tr>
                    <td className="border border-border px-4 py-2">BlackRock NURS Consensus</td>
                    <td className="border border-border px-4 py-2">Legal & General Multi Index</td>
                  </tr>
                  <tr>
                    <td className="border border-border px-4 py-2">Santander Asset Management Atlas</td>
                    <td className="border border-border px-4 py-2">Standard Life MyFolio Markets</td>
                  </tr>
                  <tr>
                    <td className="border border-border px-4 py-2">Zurich Sterling Horizon Multi-Asset</td>
                    <td className="border border-border px-4 py-2">Vanguard LifeStrategy</td>
                  </tr>
                  <tr>
                    <td className="border border-border px-4 py-2">Standard Life MyFolio Multi Manager</td>
                    <td className="border border-border px-4 py-2">Standard Life Conventional</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>5.3. Deriving the Strategic Asset Allocation (SAA)</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="mb-4">
              The neutral Strategic Asset Allocation for each Model Portfolio is defined as the weighted-average asset allocation of 
              the constituent funds within its corresponding AWM Composite Benchmark. This process is executed quarterly:
            </p>
            <ol className="list-decimal list-inside space-y-2 ml-4">
              <li>
                <strong>Data Ingestion:</strong> Using FE Analytics, the latest available full holdings data for every fund within the 
                five composite benchmarks is downloaded.
              </li>
              <li>
                <strong>Asset Class Mapping:</strong> Each underlying holding is mapped to a predefined asset class (e.g., UK Equities, 
                Global Government Bonds, Infrastructure).
              </li>
              <li>
                <strong>Aggregation:</strong> The asset class weights are aggregated up to the benchmark level, respecting the 
                equal-weighting of the constituent fund houses.
              </li>
              <li>
                <strong>SAA Publication:</strong> The resulting asset class percentages become the official SAA for the corresponding 
                CBAM Model Portfolio for the forthcoming quarter.
              </li>
            </ol>
            <p className="mt-4">
              This SAA is the neutral position around which the portfolios are managed and the baseline against which all tactical 
              deviations are measured.
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>5.4. The Annual Benchmark Review Cycle</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="mb-4">
              The SAA year begins in January, culminating in a formal review by the Investment Committee in March. This cycle ensures 
              the benchmarks and their derived SAAs remain robust and fit for purpose.
            </p>
            <ul className="list-disc list-inside space-y-2 ml-4">
              <li>
                <strong>January-February: Data Gathering & Analysis:</strong> The Investment Analyst team assesses the performance and 
                risk characteristics of the existing benchmarks and their constituents over the prior year. They also screen the market 
                for any new fund ranges that may meet the inclusion criteria and conduct initial due diligence.
              </li>
              <li>
                <strong>Early March: IC Benchmark Summit:</strong> The Investment Committee convenes for a dedicated session to review 
                the benchmark methodology, the composition of the Approved Fund House List, and the derived SAAs. Any proposal to add 
                or remove a fund house, or to amend the construction methodology, is formally debated.
              </li>
              <li>
                <strong>Late March: Board Noting:</strong> Any material changes approved by the IC (e.g., adding a new fund house to 
                the list) are formally noted by the Board in its next meeting, ensuring top-level oversight of the core investment 
                process.
              </li>
            </ul>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>5.5. Role of Quantitative Optimisation</CardTitle>
          </CardHeader>
          <CardContent>
            <p>
              Quantitative optimisation models, such as mean-variance or Black-Litterman optimisers, have a strictly secondary and 
              subordinate role within our investment process. They may be used by the risk function as a secondary tool to stress-test 
              the benchmark's SAA under various macroeconomic scenarios or to analyse the portfolio's position on a theoretical 
              efficient frontier. However, they will not be used to determine the primary SAA. The SAA is exclusively derived from the 
              AWM Composite Benchmark as described in Section 5.3.
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>5.6. Materiality Thresholds and Board Oversight</CardTitle>
          </CardHeader>
          <CardContent>
            <p>
              A change to the SAA is considered material if it results from a change in the benchmark construction methodology itself 
              (e.g., moving from equal-weighting to AUM-weighting) or a change in the composition of the Approved Fund House List. Such 
              material changes must be approved by the Investment Committee and subsequently ratified by the Board. Non-material shifts 
              in the SAA that occur naturally due to the quarterly re-derivation from the benchmark's underlying holdings do not require 
              specific Board approval but are documented and available for review.
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>5.7. Communication to Stakeholders</CardTitle>
          </CardHeader>
          <CardContent>
            <p>
              Once the quarterly SAA is finalised, the updated weights feed into factsheets, adviser presentation decks, and platform 
              model codes. An FAQ document explains the benchmark-driven rationale in plain English for advisers and clients, ensuring 
              Consumer Duty requirements for transparency are satisfied. Platform partners receive rebalance files in the appropriate 
              format, minimising operational risk during transition.
            </p>
          </CardContent>
        </Card>
      </div>
    ),
  },

  {
    number: 6,
    title: "Permitted Asset Classes & Ranges",
    description: "Design principles, sleeves, suite-specific universe constraints and ranges",
    content: (
      <div className="space-y-4">
        <Card>
          <CardContent className="pt-6">
            <p>
              A clear statement of <em>what we may invest in</em>, and in <em>what proportion</em>, is essential for three reasons. First,
              it provides advisers with line-of-sight over the ingredients that shape client outcomes. Second, it gives the Risk and
              Compliance functions a yardstick against which to measure drift or mandate creep. Third, it assures the FCA that our
              portfolio construction process remains inside well-defined guardrails and therefore satisfies PROD's product-design and
              oversight rules.
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader><CardTitle>6.1. Design Principles</CardTitle></CardHeader>
          <CardContent>
            <p className="mb-3">In agreeing asset-class ranges the Board weighs four considerations:</p>
            <ul className="list-disc pl-6 space-y-2">
              <li><strong>Diversification Value:</strong> each sleeve must add incremental return, reduce risk, or do both once correlations are taken into account.</li>
              <li><strong>Liquidity:</strong> securities must be realisable quickly under stressed market conditions so that the 95% NAV liquidity promise in Section 10 can be honoured.</li>
              <li><strong>Data Transparency:</strong> robust pricing, ESG scoring and risk metrics must be available.</li>
              <li><strong>Regulatory Clarity:</strong> UCITS status, UK-tax wrapper eligibility and SDR labelling considerations inform whether an asset class may be included.</li>
            </ul>
          </CardContent>
        </Card>

        <Card>
          <CardHeader><CardTitle>6.1a. Suite-Specific Universe Constraints</CardTitle></CardHeader>
          <CardContent>
            <p className="mb-3">While the general universe of assets is defined below, specific portfolio suites operate with explicit constraints to meet their mandates:</p>
            <ul className="list-disc pl-6 space-y-2">
              <li><strong>P, MM, and Dist Suites:</strong> may invest across the full universe of permitted instruments, including OEICs, Exchange Traded Funds (ETFs) and Investment Trusts (ITs).</li>
              <li><strong>R Suite:</strong> strictly limited to <strong>Open Ended Investment Companies (OEICs) only</strong>. The use of ETFs and ITs is prohibited for this suite. The R Suite will adhere to a "passive-first" principle, where active funds are only used if a passive alternative for the desired exposure is unavailable.</li>
            </ul>
          </CardContent>
        </Card>

        <Card>
          <CardHeader><CardTitle>6.2. Core Fixed-Income Sleeve</CardTitle></CardHeader>
          <CardContent>
            <p className="mb-3">
              The fixed-income allocation performs three primary jobs: (1) to act as a ballast to portfolio volatility, (2) to provide
              reliable liquidity, and (3) to offer a potential hedge in equity drawdowns. The sleeve is composed of a diversified mix of
              sovereign and corporate debt, including both investment-grade and sub-investment-grade securities where appropriate for the
              portfolio's mandate and risk budget.
            </p>
            <ul className="list-disc pl-6 space-y-2">
              <li><strong>Global sovereign bonds</strong> form the foundational part of the sleeve (predominantly UK gilts and G7 treasuries). Maximum allocation is at the discretion of the CIO; exposure may not fall below 3% in the Adventurous profile to ensure a minimum level of liquidity.</li>
              <li><strong>Investment-grade corporate credit</strong> (sterling, dollar and euro) is capped at 40% to avoid concentration in idiosyncratic credit risk.</li>
              <li><strong>High-yield corporate credit</strong> may be included to enhance income generation, particularly within the Distribution ("Dist") suite. Bonds rated below BBB- (or equivalent) are permitted. Allocation is managed dynamically by the Investment Committee and is constrained by the overall risk budget (Section 11).</li>
            </ul>
          </CardContent>
        </Card>

        <Card>
          <CardHeader><CardTitle>6.3. Growth Engine: Global Equities</CardTitle></CardHeader>
          <CardContent>
            <p>
              Equities are the principal long-run return driver and therefore occupy the largest allocation range: from 15% in the Risk
              Type 1 models to up to 100% in the Risk Type 5 models. To avoid home-bias concentration, regional weights are guided by the
              benchmark-derived SAA, which typically reflects broad global market-cap distributions like the MSCI ACWI.
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader><CardTitle>6.4. Diversifiers: Alternatives Bucket</CardTitle></CardHeader>
          <CardContent>
            <p className="mb-3">The alternatives sleeve, capped at 10% across all risk bands, houses three categories designed to behave differently from traditional equities and bonds:</p>
            <ul className="list-disc pl-6 space-y-2">
              <li><strong>Listed infrastructure trusts</strong>, whose cash flows are often explicitly linked to inflation indices.</li>
              <li><strong>Global real estate investment trusts (REITs)</strong>, which add real-asset exposure but remain daily-liquid via exchange listings.</li>
              <li><strong>Market-neutral absolute-return UCITS funds</strong>, which target low beta and allocate across various alternative strategies.</li>
            </ul>
            <p className="mt-3">Each sub-category must represent at least 2% of NAV to be meaningful yet may never exceed 50% of the alternatives sleeve individually.</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader><CardTitle>6.5. Factor-Investing Satellites</CardTitle></CardHeader>
          <CardContent>
            <p>
              In markets where evidence shows persistent factor premia, we may deploy satellite ETFs tilted toward value, quality, and
              low volatility. Factor sleeves are bounded between 0% and 8% of NAV to prevent style drift. The factor budget sits within,
              not on top of, the equity allocation so that overall risk budgets remain intact.
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader><CardTitle>6.6. Thematic Allocations</CardTitle></CardHeader>
          <CardContent>
            <p>
              We also reserve up to 5% of NAV for thematic asset allocations, for example, clean energy, cybersecurity or
              ageing-population plays, where long-term secular growth intersects with investable liquidity. Thematic positions emerge
              from the monthly Thematic Ideas Discussion chaired by the Deputy CIO.
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader><CardTitle>6.7. Cash and Cash Equivalents</CardTitle></CardHeader>
          <CardContent>
            <p>
              A minimum 0.5% cash allocation is maintained across all models for liquidity operations. Cash may rise to 10% temporarily
              if market conditions warrant. Any cash position that deviates by more than 1% from the benchmark's SAA for more than three
              days prompts an automated alert to Portfolio Management, necessitating an explanation to the CIO.
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader><CardTitle>6.8. Range Review and Adjustment Mechanism</CardTitle></CardHeader>
          <CardContent>
            <p>
              The permitted ranges are reviewed annually subsequent to the SAA cycle. The Risk team back-tests whether the existing
              corridors would have unduly constrained the portfolio's ability to track its benchmark. Any upward revision to a hard cap,
              such as raising alternatives from 10% to 12%, qualifies as a material change and thus requires Board approval under Section 1.5.
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader><CardTitle>6.9. ESG and Data-Availability Gatekeeping</CardTitle></CardHeader>
          <CardContent>
            <p>
              For asset classes considered for inclusion in the standard portfolio ranges, the Data Governance Council will assess data
              availability, ensuring liquidity data passes a minimum three-year history test. For asset classes being considered
              specifically for the 'Integrity' ESG portfolio range (launching 2026), the ESG Working Group will conduct an additional
              assessment to ensure ESG factors are measurable and align with the range's mandate. The Council maintains a publicly
              shareable Investable Universe Register.
            </p>
          </CardContent>
        </Card>
      </div>
    ),
  },

  {
    number: 7,
    title: "Derivatives, Leverage & Collateral",
    description: "Permitted instruments, leverage framework, counterparty and collateral controls",
    content: (
      <div className="space-y-4">
        <Card>
          <CardContent className="pt-6">
            <p>
              The Group recognises that modern portfolio management can draw on derivative strategies for hedging, duration management,
              and efficient implementation. Accordingly, this section formalises the conditions under which the MPS may employ certain
              instruments, while maintaining robust safeguards to protect client capital.
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader><CardTitle>7.1. Philosophical Stance on Derivatives</CardTitle></CardHeader>
          <CardContent>
            <p>
              We endorse a measured and primarily tactical use of derivatives for the purposes of risk management, duration management
              and gaining efficient market exposure. The overarching principle is that any derivative should either (a) reduce downside
              risk more efficiently than cash instruments, or (b) provide a more cost-effective or liquid method of achieving an
              exposure consistent with the SAA or an approved TAA tilt. Speculative, return-seeking derivative usage is generally not
              permitted at the portfolio level, though it may be present within underlying fund holdings.
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader><CardTitle>7.2. Permitted Instrument Set</CardTitle></CardHeader>
          <CardContent>
            <p className="mb-3">Four categories of derivatives and derivative-based strategies are authorised:</p>
            <ul className="list-disc pl-6 space-y-2">
              <li><strong>Currency Forwards:</strong> hedging non-Sterling currency exposures back to GBP. The hedge ratio may vary tactically between 0% and 100% at the CIO's discretion.</li>
              <li><strong>Bond Futures:</strong> managing portfolio duration. Net duration shift implemented via futures is limited to 1.5 years relative to the benchmark's duration.</li>
              <li><strong>Equity Index Futures:</strong> may be used to implement tactical long exposures to broad equity regions or factors. Short index exposure is not permitted.</li>
              <li><strong>UCITS Funds with Systematic FX-Hedging Share Classes:</strong> ETFs and OEICs offering GBP-hedged share classes are permitted. These do not count toward the standalone forward cap but their hedge ratios feed the portfolio's aggregate currency-beta calculation.</li>
            </ul>
          </CardContent>
        </Card>

        <Card>
          <CardHeader><CardTitle>7.3. Leverage Framework</CardTitle></CardHeader>
          <CardContent>
            <p>
              Direct leverage at the portfolio level is prohibited. The gross exposure, calculated using the commitment approach, must
              not exceed 100% of NAV. This does not preclude investment in underlying funds which may themselves use leverage in
              accordance with their UCITS-compliant prospectuses.
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader><CardTitle>7.4. Short Exposure Controls</CardTitle></CardHeader>
          <CardContent>
            <p>
              Direct short positions are prohibited. The MPS will not use futures, options, or other derivatives to establish a net
              short exposure to any asset class, region, or factor. This does not preclude investment in underlying funds which may
              themselves use leverage in accordance with their UCITS-compliant prospectuses.
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader><CardTitle>7.5. Counterparty & Prime Brokerage</CardTitle></CardHeader>
          <CardContent>
            <p>
              All derivative trades are executed through exchange-traded instruments. The MPS holds no prime brokerage accounts.
              Counterparty risk is monitored daily, with exposure to any single counterparty capped at 5% of NAV, consistent with UCITS
              diversification rules (though the MPS is not bound to UCITS rules).
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader><CardTitle>7.6. Collateral & Margin for Leveraged Positions</CardTitle></CardHeader>
          <CardContent>
            <p>As CBAM does not use direct derivative positions, there is no need for ourselves to maintain collateral and margin for said positions.</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader><CardTitle>7.7. Risk Budget Integration</CardTitle></CardHeader>
          <CardContent>
            <p>
              All derivative positions feed into the Portfolio-Level VaR and Stress-Testing Engine in RiskEngine.gsheets. The Risk team
              recalculates 95% one-year VaR after each trade is booked. The delta-adjusted exposure of derivatives is included in the
              calculation of portfolio weights for risk monitoring purposes.
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader><CardTitle>7.8. Governance and Exceptional Authority</CardTitle></CardHeader>
          <CardContent>
            <p>
              The CIO holds standing authority to deploy permitted derivatives within the policy limits. Any use of a non-permitted
              instrument or a breach of the stated limits requires prior IC approval and post-hoc Board ratification within ten
              business days.
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader><CardTitle>7.9. Back-Testing and Scenario Validation</CardTitle></CardHeader>
          <CardContent>
            <p>
              Prior to using any new type of permitted derivative (such as leveraged ETFs), the Research team will conduct a back-test
              to model its impact on portfolio volatility and tracking error. Stress scenarios will be run to confirm that the
              instrument would not cause a breach of VaR ceilings under 2008 or 2020 style shocks.
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader><CardTitle>7.10. Audit & Review</CardTitle></CardHeader>
          <CardContent>
            <p>
              Internal Audit will conduct a review of derivative usage within 12 months of any new strategy activation, verifying trade
              documentation, margin procedures, and disclosure accuracy. Findings and any remediation will be reported to the Board
              Audit Committee.
            </p>
          </CardContent>
        </Card>
      </div>
    ),
  },

  {
    number: 8,
    title: "Tactical Asset Allocation Guardrails",
    description: "Three-pillar signal engine, sizing, governance and emergency protocols",
    content: (
      <div className="space-y-4">
        <Card>
          <CardContent className="pt-6">
            <p>
              While the benchmark-derived Strategic Asset Allocation sets the long-term compass bearing, Tactical Asset Allocation (TAA)
              allows the Investment Team to adjust sails as market winds shift. Section 8 therefore codifies a decision-making matrix
              that channels macro views into finite, risk-capped portfolio tilts. The entire TAA framework operates relative to the
              benchmark's SAA. A tactical decision is, by definition, an active bet against the consensus allocation of the managers
              within our benchmark. This imposes a high burden of proof and requires a disciplined, transparent, and tightly
              risk-managed process.
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader><CardTitle>8.1. The Three-Pillar Signal Engine</CardTitle></CardHeader>
          <CardContent>
            <p className="mb-3">At the core of our TAA process lies a three-pillar model designed to identify potential deviations from the benchmark's SAA.</p>
            <ul className="list-disc pl-6 space-y-2">
              <li><strong>Valuation</strong> — measures whether an asset class is rich or cheap relative to its own history (e.g. CAPE for equities, real yield for bonds).</li>
              <li><strong>Momentum</strong> — tracks price persistence over six- and twelve-month windows.</li>
              <li><strong>Policy</strong> — gauges the stance of fiscal and monetary authorities via indices like the Global Financial Conditions Index.</li>
            </ul>
            <p className="mt-3">
              Each week, the Research team converts raw metrics into z-scores. These are aggregated to produce a composite score ranging
              from +3 (strong potential overweight signal) to –3 (strong potential underweight signal) for each major asset class,
              relative to the benchmark's current positioning.
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader><CardTitle>8.1a. Application of TAA by Suite</CardTitle></CardHeader>
          <CardContent>
            <ul className="list-disc pl-6 space-y-2">
              <li><strong>P, MM, and Dist Suites:</strong> eligible for tactical tilts as determined by the IC. The "P" suite has the widest latitude for unconstrained tilts where exceptional value is perceived.</li>
              <li><strong>R Suite:</strong> Tactical Asset Allocation is <strong>prohibited</strong>. These portfolios must adhere strictly to the Strategic Asset Allocation, subject to their specific rebalancing tolerances.</li>
            </ul>
          </CardContent>
        </Card>

        <Card>
          <CardHeader><CardTitle>8.2. Eligibility and Alignment Rules</CardTitle></CardHeader>
          <CardContent>
            <p>
              To avoid actioning spurious signals, a tilt becomes eligible for consideration by the IC only when at least two of the
              three pillars concur on direction and the third is not in outright opposition. For example, if Valuation and Policy both
              signal an overweight versus the benchmark while Momentum is flat, an overweight is permissible; if Momentum signals a
              strong underweight, the signal is considered conflicted and is discarded. This ensures a degree of consensus before any
              active risk is taken.
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader><CardTitle>8.3. Position-Sizing Logic</CardTitle></CardHeader>
          <CardContent>
            <p>
              Once a tilt passes eligibility, its size is determined primarily by its contribution to the portfolio's <strong>tracking
              error</strong> relative to the AWM Composite Benchmark. The size of any tactical tilt must be consistent with the overall
              tracking error budget for the portfolio, as defined in Section 11. A tilt with a strong signal and low correlation to
              existing bets may be sized larger, while a tilt that would concentrate risk will be scaled back, regardless of the
              signal's strength. The marginal contribution to portfolio VaR and liquidity are also key considerations.
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader><CardTitle>8.4. Turnover and Cost Discipline</CardTitle></CardHeader>
          <CardContent>
            <p>
              Excessive trading undermines net returns, so turnover is capped at 40% of NAV per annum. The TAA engine includes a
              transaction-cost module that estimates bid-ask and market-impact slippage for each proposed trade. If cumulative
              projected cost exceeds five basis points in a calendar month, lower-conviction tilts are deferred. This rule ensures that
              the pursuit of tactical alpha never becomes a self-inflicted drag on performance.
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader><CardTitle>8.5. Implementation Timeline and Operational Workflow</CardTitle></CardHeader>
          <CardContent>
            <p>
              Approved TAA changes are implemented as efficiently as possible. As the portfolios operate on wrap platforms, the
              implementation timeline is subject to the operational constraints of those platforms and the settlement periods of the
              underlying instruments. ETFs are typically subject to T+2 settlement, while UCITS funds are settled according to their
              own specific dealing cycles. The RiskEngine.gsheets workbook recalculates predicted tracking error and VaR post-trade,
              and the TAA Attribution ledger is updated to measure the tilt's contribution to performance on a rolling basis.
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader><CardTitle>8.6. Governance Oversight</CardTitle></CardHeader>
          <CardContent>
            <p>
              Every tilt must be minuted by the IC with its economic rationale, expected lifespan, and explicit exit criteria. A tilt
              that fails to add value relative to the benchmark after two quarters is automatically scheduled for review and possible
              unwind. The Risk Oversight Panel receives a quarterly TAA Effectiveness Report, containing hit rates (percentage of
              profitable tilts), cumulative alpha, and tracking error utilisation.
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader><CardTitle>8.7. Case Study: A Hypothetical 2023 Japan Overweight</CardTitle></CardHeader>
          <CardContent>
            <p>
              In April 2023, assume the AWM Composite Benchmark held an 8% allocation to Japanese equities. Our signal engine noted a
              strong valuation case (z-score +1.5) and a positive policy shift (z-score +1.2), while momentum was neutral. The IC
              approved a 3 percentage point overweight, taking the portfolio's allocation to 11%. This decision was an explicit bet
              that the consensus of managers in the benchmark was under-allocated to Japan. The size was calibrated to use approximately
              15% of the portfolio's annual tracking error budget. Subsequent performance attribution would transparently measure the
              value added (or subtracted) by this specific deviation from the benchmark's SAA.
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader><CardTitle>8.8. Emergency Tilt Protocol</CardTitle></CardHeader>
          <CardContent>
            <p>
              In periods of extreme market dislocation (e.g., the 2020 COVID crash), the CIO may initiate an Emergency Tilt Protocol
              outside the regular IC cycle. Any trade booked under this protocol must be designed to reduce risk (i.e., move the
              portfolio closer to its benchmark SAA or to cash) and expires automatically after ten business days unless expressly
              re-authorised by the IC. This ensures that crisis decisions do not ossify into long-term, un-governed active positions.
            </p>
          </CardContent>
        </Card>
      </div>
    ),
  },

  {
    number: 9,
    title: "Responsible Investment Vision and 2025 Transition Plan – Forward-Looking Only",
    description: "Prospective ESG framework, 'Integrity' ESG-labelled suite roadmap and lean governance",
    content: (
      <div className="space-y-4">
        <Card>
          <CardContent className="pt-6">
            <p>
              The following section sets out plans and milestones; none of the ESG-related elements described are currently live within
              the Model Portfolio Service (MPS). All language is drafted in the conditional or future tense to make clear that every
              initiative remains prospective until the Board signs off and operational workstreams reach completion.
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader><CardTitle>9.1. Current Baseline: No Formal ESG Framework</CardTitle></CardHeader>
          <CardContent>
            <p>
              As at October 2025, the MPS operates on primarily financial selection criteria. No formal exclusion screens, weighted ESG
              scores, or stewardship policies are embedded in the portfolio management process. Responsibility for proxy voting and
              engagement remains entirely with underlying fund managers; the Group neither instructs nor monitors their ESG actions in
              any systematic way.
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader><CardTitle>9.2. Strategic Aim: Establish a Parallel ESG-Labelled Portfolio Suite by Q4 2026</CardTitle></CardHeader>
          <CardContent>
            <p>
              Recognising adviser demand and forthcoming FCA Sustainability Disclosure Requirements (SDR), the Board intends to create
              an ESG-labelled model portfolio range, provisionally named 'Integrity'. The core MPS will remain ESG-neutral, offering
              clients a choice. Workstream Integrity is tasked with designing the ESG suite, ensuring it meets SDR labelling rules
              while operating within existing resource constraints (two investment staff) and data availability (FE Analytics plus
              open-source feeds).
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader><CardTitle>9.3. Roadmap in Line with SDR and Feasible on FE Analytics</CardTitle></CardHeader>
          <CardContent>
            <ul className="list-disc pl-6 space-y-2">
              <li><strong>Phase 1 – Framework Definition (Q3 2025):</strong> the EWG drafts screening and integration rules mapped to SDR consumer labels (Sustainability Focus, Improvers, Impact). Only data fields already provided by FE Analytics or freely accessible datasets will be used.</li>
              <li><strong>Phase 2 – Data & System Build-Out (Q4 2025):</strong> a basic script pulls FE Analytics ESG CSV files into the existing data warehouse. Compliance rules coded in FE Analytics' Screening tool flag holdings that breach emerging SDR criteria.</li>
              <li><strong>Phase 3 – Pilot & Launch (Q1/Q2 2026):</strong> assuming Board approval and satisfactory back-tests, ESG-labelled portfolios launch on two adviser platforms for a six-month pilot. Factsheets will carry SDR label icons and FE Analytics carbon-footprint charts. Broader distribution decision scheduled for Q2 2026.</li>
            </ul>
          </CardContent>
        </Card>

        <Card>
          <CardHeader><CardTitle>9.4. Interim Stewardship and Voting Approach (2025-2026)</CardTitle></CardHeader>
          <CardContent>
            <p>
              Given staffing constraints, the Group will not conduct direct engagements during 2025-26. Instead, underlying fund
              managers will continue to exercise proxy votes. Twice a year, the Investment Analyst will download managers' publicly
              available voting reports, store them on Google Drive, and prepare a summary for the Board. No additional stewardship
              activity is planned until the ESG-labelled suite is operational.
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader><CardTitle>9.5. Lean Governance Model Compatible with Two-Person Team</CardTitle></CardHeader>
          <CardContent>
            <p>
              To keep governance light but credible, the EWG will hold a single 30-minute ESG Checkpoint call each quarter. A script
              will refresh FE Analytics ESG metrics and update a one-row "ESG-Mini-Log" in Excel. This minimalist loop will consume no
              more than one hour per quarter, meeting both regulatory expectations and resource realities.
            </p>
          </CardContent>
        </Card>
      </div>
    ),
  },

  {
    number: 10,
    title: "Liquidity & Cash Management Policy",
    description: "LiquidityEngine.gsheets, LCR, cash buffers, stress testing and Board reporting",
    content: (
      <div className="space-y-4">
        <Card>
          <CardContent className="pt-6">
            <p>
              Liquidity management must be reliable yet frugal, because the Investment desk relies solely on FE Analytics and freely
              available market data. Proprietary feeds such as Bloomberg or Refinitiv are outside our budget, so the policy leans on
              open APIs and the fund-liquidity metrics already bundled into FE Analytics. All calculations are performed within a
              dedicated Google Sheets workbook, LiquidityEngine.gsheets.
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader><CardTitle>10.1. Data Ingestion and Metric Construction</CardTitle></CardHeader>
          <CardContent>
            <p className="mb-3">At 07:00 GMT each trading day, a script harvests datasets and funnels them into the LiquidityEngine.gsheets workbook:</p>
            <ul className="list-disc pl-6 space-y-1 mb-3">
              <li>Ingests the end-of-day holdings CSV from the custodian portal.</li>
              <li>Calls a public API (e.g., Yahoo Finance) to pull closing prices and average daily volumes for exchange-traded instruments.</li>
              <li>Imports an FE Analytics file containing operational liquidity attributes for mutual funds (dealing cut-off, settlement days, notice periods).</li>
            </ul>
            <p>
              These data drive two headline metrics. The <strong>Bid-Ask Stress Cost</strong> estimates the cost of liquidation under
              stressed conditions. The <strong>Liquidity Coverage Ratio (LCR)</strong> estimates the percentage of the portfolio that
              could realistically be converted to cash within five trading days. It assumes ETFs are saleable up to a stressed
              percentage of their average daily volume, while mutual funds contribute liquidity according to a conservative "10% of NAV
              per day" rule, which is periodically sense-checked via questionnaires to fund houses.
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader><CardTitle>10.2. Operational Response Protocol</CardTitle></CardHeader>
          <CardContent>
            <p>
              If the calculated LCR falls below 1.0 (meaning less than 100% of the target NAV can be liquidated in five days), the
              workbook cell turns red. This triggers an automated email alert to the SPM and IA. A red LCR requires a same-day Teams
              call to decide on containment actions, such as staggering the exit of an illiquid holding or rebalancing into a more
              liquid ETF. The rationale is typed directly into a "Liquidity Note" column of the workbook, creating a time-stamped audit
              trail.
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader><CardTitle>10.3. Cash Buffer Policy</CardTitle></CardHeader>
          <CardContent>
            <p>
              A cash buffer is maintained consistent with the benchmark SAA. Daily balances are visible on the custodian portal. If
              cash deviates by more than 1% from the benchmark's cash weighting for three consecutive days, the IA prepares a note to
              the SPM to make them aware.
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader><CardTitle>10.4. Dynamic Margin Buffer</CardTitle></CardHeader>
          <CardContent>
            <p>See section 7.6.</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader><CardTitle>10.5. Stress Testing with Public Data</CardTitle></CardHeader>
          <CardContent>
            <p>
              Once per quarter, the IA runs a stress macro that applies bid-ask and volume shocks derived from public crisis datasets
              (e.g., 2008 financial crisis, 2020 COVID crash). The stress template recalculates LCR under those shocks; if any
              profile's LCR dips below 1.0, the SPM drafts a one-paragraph mitigation note for the Board pack.
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader><CardTitle>10.6. Board Reporting</CardTitle></CardHeader>
          <CardContent>
            <p>
              A slide in the quarterly Board pack links directly to the LiquidityEngine.gsheets workbook. The slide auto-displays a
              sparkline of LCR, Bid-Ask Stress Cost, and cash percentage. Commentary is one sentence unless the status is red, in which
              case the SPM adds a brief narrative.
            </p>
          </CardContent>
        </Card>
      </div>
    ),
  },

  {
    number: 11,
    title: "Risk Appetite & Quantitative Limits",
    description: "Volatility, VaR, Drawdown and Tracking Error ceilings by profile, with alert workflow",
    content: (
      <div className="space-y-4">
        <Card>
          <CardContent className="pt-6">
            <p>
              All risk monitoring lives in a single Google Sheets workbook, RiskEngine.gsheets, refreshed automatically by Google Apps
              Script. This design eliminates the need for on-premise servers, leveraging FE Analytics daily NAV exports and open-source
              price feeds. The philosophy is transparent, reproducible calculations that a two-person desk can manage day-to-day. The
              entire framework is anchored to the risk profile of the AWM Composite Benchmarks, with a primary focus on managing the
              risk of deviation from the core strategy.
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader><CardTitle>11.1. Core Risk Lenses and Data Sources</CardTitle></CardHeader>
          <CardContent>
            <p className="mb-3">Four quantitative lenses govern each risk profile:</p>
            <ul className="list-disc pl-6 space-y-2">
              <li><strong>Ex-ante Volatility</strong> — calculated directly in Google Sheets from the daily price series of the portfolio and its benchmark.</li>
              <li><strong>One-year 95% Value-at-Risk (VaR)</strong> — produced by a custom Apps Script function <code>histVar()</code> running a historical simulation on 3,000 days of price data. Assessed relative to the benchmark VaR.</li>
              <li><strong>Maximum Drawdown</strong> — computed by an Apps Script helper <code>maxDD()</code> scanning the five-year rolling path of the return series, for both portfolio and benchmark.</li>
              <li><strong>Tracking Error</strong> — the primary measure of active risk. A custom Apps Script function calculates both predicted (ex-ante) and realised (ex-post) tracking error of the portfolio relative to its AWM Composite Benchmark.</li>
            </ul>
          </CardContent>
        </Card>

        <Card>
          <CardHeader><CardTitle>11.2. Risk Ceilings by Profile</CardTitle></CardHeader>
          <CardContent>
            <p className="mb-4">The risk ceilings for each portfolio are calibrated to be consistent with the risk profile of its corresponding benchmark, with an explicit budget for active risk.</p>
            <div className="overflow-x-auto">
              <table className="min-w-full border border-border">
                <thead className="bg-muted">
                  <tr>
                    <th className="border border-border px-3 py-2 text-left">Risk Profile</th>
                    <th className="border border-border px-3 py-2 text-left">Volatility Ceiling (%)</th>
                    <th className="border border-border px-3 py-2 text-left">VaR (95%, 1yr) Ceiling (%)</th>
                    <th className="border border-border px-3 py-2 text-left">Max Drawdown Ceiling (%)</th>
                    <th className="border border-border px-3 py-2 text-left">Tracking Error Ceiling (p.a.) (%)</th>
                  </tr>
                </thead>
                <tbody>
                  <tr><td className="border border-border px-3 py-2">Defensive</td><td className="border border-border px-3 py-2">6</td><td className="border border-border px-3 py-2">8</td><td className="border border-border px-3 py-2">-10</td><td className="border border-border px-3 py-2">1.5</td></tr>
                  <tr><td className="border border-border px-3 py-2">Cautious</td><td className="border border-border px-3 py-2">8</td><td className="border border-border px-3 py-2">10</td><td className="border border-border px-3 py-2">-14</td><td className="border border-border px-3 py-2">2.0</td></tr>
                  <tr><td className="border border-border px-3 py-2">Balanced</td><td className="border border-border px-3 py-2">10</td><td className="border border-border px-3 py-2">12</td><td className="border border-border px-3 py-2">-18</td><td className="border border-border px-3 py-2">2.5</td></tr>
                  <tr><td className="border border-border px-3 py-2">Growth</td><td className="border border-border px-3 py-2">13</td><td className="border border-border px-3 py-2">16</td><td className="border border-border px-3 py-2">-25</td><td className="border border-border px-3 py-2">3.0</td></tr>
                  <tr><td className="border border-border px-3 py-2">Aggressive</td><td className="border border-border px-3 py-2">16</td><td className="border border-border px-3 py-2">20</td><td className="border border-border px-3 py-2">-30</td><td className="border border-border px-3 py-2">3.5</td></tr>
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader><CardTitle>11.3. Daily Refresh and Alert Workflow</CardTitle></CardHeader>
          <CardContent>
            <p className="mb-3">
              A time-driven Apps Script trigger runs at 06:30 GMT. It ingests the previous day's data, recalculates all risk metrics
              for both the portfolios and their benchmarks, and writes the outputs into the RiskDashboard sheet.
            </p>
            <ul className="list-disc pl-6 space-y-1">
              <li>If any metric exceeds 95% of its ceiling, the script paints the cell <strong>amber</strong>.</li>
              <li>If it crosses 100%, it paints the cell <strong>red</strong>.</li>
            </ul>
            <p className="mt-3">Immediately after colour-coding, the script composes an HTML email with the table embedded and sends it to the SPM and IA using <code>MailApp.sendEmail()</code>.</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader><CardTitle>11.4. Escalation Path</CardTitle></CardHeader>
          <CardContent>
            <ul className="list-disc pl-6 space-y-2">
              <li><strong>Amber (95-100% of limit)</strong> prompts the IA to note the breach at the weekly desk huddle.</li>
              <li><strong>Red (&gt;100%)</strong> requires immediate action. For a tracking error breach, further trades that would increase active risk are halted. The SPM must draft a one-pager for the Board within 48 hours, detailing corrective trades (typically to move the portfolio closer to its benchmark SAA) and the expected timeline for resolution.</li>
            </ul>
          </CardContent>
        </Card>

        <Card>
          <CardHeader><CardTitle>11.5. Scenario Replay</CardTitle></CardHeader>
          <CardContent>
            <p>
              Quarterly, a separate Apps Script pulls historical returns for the 2008 and 2020 crisis windows, applies them to current
              portfolio and benchmark weights, and writes simulated VaR/drawdowns to the StressTest sheet. The key assessment is not
              just the absolute loss, but whether the portfolio's drawdown significantly exceeded its benchmark's drawdown during the
              simulated crisis. Any simulated breach of the tracking error budget under stress triggers a red flag in the Board pack.
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader><CardTitle>11.6. Board Reporting</CardTitle></CardHeader>
          <CardContent>
            <p>
              A slide in the quarterly Board pack embeds a live chart from Google Sheets via the "Linked chart" feature. The chart
              displays key metrics like realised tracking error and VaR relative to their ceilings. When a cell turns red in the
              underlying sheet, the chart bar crosses the ceiling line, and the SPM expands the commentary accordingly.
            </p>
          </CardContent>
        </Card>
      </div>
    ),
  },

  {
    number: 12,
    title: "Performance Measurement & Attribution",
    description: "AWM Composite Benchmark as primary measure, peer benchmarks, attribution analysis",
    content: (
      <div className="space-y-4">
        <Card>
          <CardContent className="pt-6">
            <p>
              Performance monitoring must be accurate enough to meet Board and FCA expectations yet light enough for a lean investment
              desk armed only with FE Analytics and Google Workspace. All calculations live inside the <code>PerformanceEngine.gsheets</code>
              workbook and are refreshed via Apps Script. The entire framework is reoriented to place the AWM Composite Benchmark at
              the centre of the analysis, providing a clear, objective measure of the investment process's success.
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader><CardTitle>12.1. Data Acquisition via FE Analytics and Google Drive</CardTitle></CardHeader>
          <CardContent>
            <p>
              At the start of every working day, the Investment Analyst downloads a standard FE Analytics performance CSV that
              contains: performance for each portfolio, daily levels for each corresponding AWM Composite Benchmark, and ARC MPS
              Benchmark returns (if the start of the month). The CSV auto-lands in a <code>/Performance/DataLake</code> folder on Google
              Drive. An Apps Script trigger ingests the file, appends new rows to the Prices tab of PerformanceEngine.gsheets, and
              stamps a checksum in the Audit tab.
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader><CardTitle>12.2. Primary Benchmark: The AWM Composite Benchmark</CardTitle></CardHeader>
          <CardContent>
            <p>
              The primary and definitive measure of each portfolio's performance is its return relative to its corresponding AWM
              Composite Benchmark, as specified in Table 4.1. This is the yardstick against which the Investment Committee, the Board,
              and clients should judge the value added by our management. All key relative performance metrics, including excess return
              and information ratio, are calculated against this primary benchmark. For the Distribution suite, performance is also
              measured against its specific primary objectives of a 4%+ target yield and a total return of CPI+.
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader><CardTitle>12.3. Secondary Benchmark: Peer Group Context</CardTitle></CardHeader>
          <CardContent>
            <p>
              The AFI and ARC MPS Indices will be used as a secondary, contextual data point to understand the portfolio's positioning
              within the broader MPS peer group. This allows the Board to assess how our chosen strategy is performing relative to the
              wider market. However, the AFI and ARC index is <u>not</u> the primary measure of success or failure for our investment
              process. Persistent underperformance versus AFI or ARC MPS would trigger a review of the benchmark's composition, not
              necessarily our TAA or implementation.
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader><CardTitle>12.4. Attribution Analysis</CardTitle></CardHeader>
          <CardContent>
            <p className="mb-3">A Benchmark Weights tab holds the neutral SAA weights derived quarterly from the benchmark. Google Sheets' array functions decompose monthly excess returns relative to the benchmark into three key effects:</p>
            <ol className="list-decimal pl-6 space-y-2">
              <li><strong>Tactical Asset Allocation Effect:</strong> the value added or subtracted by the IC's decisions to deviate from the benchmark's SAA.</li>
              <li><strong>Fund Selection / Implementation Effect:</strong> the performance of the specific funds/ETFs chosen to implement the SAA relative to a like-for-like index.</li>
              <li><strong>Interaction Effect:</strong> the combined impact of TAA and selection decisions.</li>
            </ol>
            <p className="mt-3">The granularity stops at the asset class and fund level; security-level attribution is beyond current resources. Quarterly, the IA copies the attribution table into the Board slide deck via a linked chart.</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader><CardTitle>12.5. RAG Thresholds and Alert Logic</CardTitle></CardHeader>
          <CardContent>
            <ul className="list-disc pl-6 space-y-2">
              <li>Underperformance of more than 50 bps over a rolling 12-month period shows <strong>amber</strong>.</li>
              <li>Underperformance of more than 100 bps over a rolling 12-month period, or any quarter where TAA detracts more than 50 bps, shows <strong>red</strong>.</li>
            </ul>
            <p className="mt-3">Any red cell prompts the SPM to draft a 200-word Performance Note for inclusion in the next IC agenda, explaining the drivers of underperformance and any proposed remedial actions.</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader><CardTitle>12.6. Automated Board Pack Integration</CardTitle></CardHeader>
          <CardContent>
            <p>
              An Apps Script runs on quarter-end. It converts the PerformanceDashboard sheet, including the attribution analysis, into
              a PDF and drops it into the <code>/BoardPack/Slides</code> Drive folder. Because the Board deck links to that PDF,
              directors always see the freshest metrics without manual intervention.
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader><CardTitle>12.7. Audit and Compliance Oversight</CardTitle></CardHeader>
          <CardContent>
            <p>
              Google Sheets' version history plus the checksum log give Compliance a WORM-like trail. The IA exports the Audit tab as a
              CSV at year-end; Compliance stores it in the firm's document management system, meeting internal record-keeping
              requirements without extra tooling.
            </p>
          </CardContent>
        </Card>
      </div>
    ),
  },

  {
    number: 13,
    title: "Cost Governance & Value-for-Money",
    description: "OCF caps, transaction-cost analysis, VfM five-box grid and provider engagement",
    content: (
      <div className="space-y-4">
        <Card>
          <CardContent className="pt-6">
            <p>
              Client outcomes hinge not only on gross returns but also on the friction that erodes them. The Group therefore embeds
              cost surveillance directly into portfolio management, using Google Sheets dashboards powered by FE Analytics exports and
              freely available trade data. This delivers credible cost governance and Consumer Duty oversight without overstretching
              current resources.
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader><CardTitle>13.1. Cost Components and Caps</CardTitle></CardHeader>
          <CardContent>
            <p>
              Three cost lines are monitored: fund Ongoing Charges Figures (OCFs), the discretionary management fee, and trading costs
              (transaction cost analysis). For portfolio risk types 1-3 (Defensive, Cautious, Balanced), the blended OCF may not exceed
              45 basis points; portfolio risk types 4 &amp; 5 (Capital Growth &amp; Aggressive) may rise to 50 basis points. The
              discretionary management fee disclosed in the individual portfolio suite remits. Transaction costs are expected to
              average 5 basis points per annum. Summed quarterly, these elements create the portfolio's Total Cost of Ownership (TCO),
              the headline figure tabled at the Board.
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader><CardTitle>13.2. Automated Calculation Workflow</CardTitle></CardHeader>
          <CardContent>
            <p>
              On the fifth business day after quarter-end, the IA downloads the FE Analytics Charges and Costs CSV. An Apps Script
              named <code>updateCosts()</code> drops those numbers into CostEngine.gsheets, multiplies them by the quarter-end portfolio
              weights, and posts the blended OCF. The same script scrapes free London Stock Exchange trade files, matches execution
              timestamps, and measures implementation shortfall for each ETF order. A colour-coding rule formats any cell that drifts
              more than five basis points above its cap as amber, or red if the cap is breached. At 09:00 GMT the dashboard emails a
              summary to the SPM.
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader><CardTitle>13.3. Value-for-Money (VfM) Framework</CardTitle></CardHeader>
          <CardContent>
            <p>
              Beginning December 2025 the Board will conduct an annual VfM review that grades each risk profile across five boxes: net
              performance (vs. CPI+ and primary benchmark), delivered risk (vs. benchmark and limits), cost (vs. cap), service quality,
              and reporting clarity. ESG alignment will join the grid from 2026 onward, once the ESG-labelled portfolio suite is
              operational. Each box is graded Green, Amber or Red. The five-box grid forms a two-page appendix to the December Board
              pack and is made available to advisers on request, fulfilling Consumer Duty disclosure.
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader><CardTitle>13.4. Provider Engagement Cycle</CardTitle></CardHeader>
          <CardContent>
            <p>
              Following each December VfM review, the IA writes to any fund house whose share-class OCF sits in the top quartile of
              its peer group and lacks demonstrable alpha over three years. The letter seeks fee concessions or clarifies the manager's
              cost-reduction roadmap. Responses are stored in the Provider Correspondence folder on Google Drive, ready for inspection
              by Compliance or the FCA.
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader><CardTitle>13.5. Escalation</CardTitle></CardHeader>
          <CardContent>
            <p>
              If the blended OCF or TCO breaches its cap, the cost dashboard turns red and the SPM must submit a Cost Remediation Note
              to the next IC meeting. Possible remedies include switching to lower-cost share classes or replacing an underperforming
              active fund with a passive equivalent. The Board expects amber breaches to be resolved within one quarter and red
              breaches within two.
            </p>
          </CardContent>
        </Card>
      </div>
    ),
  },

  {
    number: 14,
    title: "Governance Structure & Decision Rights",
    description: "Board, Investment Committee, Risk Oversight Panel, EWG and decision matrix",
    content: (
      <div className="space-y-4">
        <Card>
          <CardContent className="pt-6">
            <p>
              A sound governance architecture protects clients by ensuring that no single individual can unilaterally alter strategic
              direction or compromise risk limits. The structure laid out below is intentionally streamlined to operate smoothly with
              the firm's existing technology stack (Google Workspace, Asana) and a lean staff complement, while still meeting
              expectations for segregation of duties and documented decision trails. The adoption of the benchmark-driven investment
              process brings greater clarity to this structure, creating a clear separation between strategic oversight, methodology
              governance, and day-to-day implementation.
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader><CardTitle>14.1. Departmental Board (Board of Directors)</CardTitle></CardHeader>
          <CardContent>
            <p>
              Ultimate fiduciary responsibility rests with the Board. It approves all Level 1 policies, including this Group Investment
              Policy. Crucially, the Board is responsible for approving the overall investment <em>philosophy</em> — in this case, the
              benchmark-driven approach. It also approves any material changes to that philosophy or its highest-level parameters, such
              as leverage ceilings or risk-budget parameters. The Board meets quarterly, with meeting packs distributed through a
              locked Google Drive folder. Decisions on material matters require a two-thirds majority.
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader><CardTitle>14.2. Investment Committee (IC)</CardTitle></CardHeader>
          <CardContent>
            <p className="mb-3">
              The IC is chaired by the CIO and comprises the SPM, IA and CEO. The IC's primary role is to govern the AWM Composite
              Benchmark and manage active risk. Its key responsibilities include:
            </p>
            <ul className="list-disc pl-6 space-y-2">
              <li><strong>Conducting the Annual Benchmark Review</strong>, including due diligence on and approval of the Approved Fund House List.</li>
              <li><strong>Approving any material changes to the benchmark construction methodology</strong> (e.g. weighting scheme, rebalancing frequency).</li>
              <li><strong>Authorising Tactical Asset Allocation tilts</strong> within Section 8 guardrails.</li>
              <li><strong>Monitoring the tracking error</strong> of the model portfolios relative to their primary benchmarks and approving any TAA expected to exceed 50% of the tracking error budget.</li>
              <li><strong>Reviewing performance attribution reports</strong> to assess the effectiveness of TAA and implementation.</li>
            </ul>
            <p className="mt-3">
              This structure creates an auditable trail: the Board approves the strategy, the IC governs the benchmark which embodies
              that strategy, and the Portfolio Managers implement against that benchmark. The IC convenes monthly, with minutes drafted
              in Google Docs and action items tracked in Asana.
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader><CardTitle>14.3. Risk Oversight Panel (ROP)</CardTitle></CardHeader>
          <CardContent>
            <p>
              Chaired by the CRO, the ROP meets monthly, usually one week before the IC. Its mandate is to examine the
              RiskEngine.gsheets dashboard, with a particular focus on tracking error and breaches of other quantitative limits. It
              challenges underlying assumptions and recommends escalations where metrics approach limits. Findings flow into a concise
              risk summary slide that the CIO presents to the Board each quarter.
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader><CardTitle>14.4. Emerging ESG Working Group (EWG)</CardTitle></CardHeader>
          <CardContent>
            <p>
              An ESG Working Group will be constituted in Q1 2026 to oversee the new ESG-labelled model portfolio range envisaged in
              Section 9. Until then, ESG considerations are handled within the IC.
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader><CardTitle>14.5. Decision Rights, Narrative Matrix</CardTitle></CardHeader>
          <CardContent>
            <ul className="list-disc pl-6 space-y-2">
              <li><strong>Investment Philosophy &amp; SAA Methodology:</strong> Board approves.</li>
              <li><strong>Benchmark Composition (Approved Fund House List):</strong> IC approves, Board ratifies.</li>
              <li><strong>Tactical Asset Allocation:</strong> IC authorises within tracking error budgets; SPM executes.</li>
              <li><strong>Cost Remediation:</strong> if the Cost Dashboard turns red, the IC must endorse the SPM's remediation plan and report back to the Board.</li>
              <li><strong>Breach Escalation:</strong> minor and moderate breaches resolved under IC supervision; major breaches trigger immediate Board notification (see Section 16).</li>
            </ul>
          </CardContent>
        </Card>

        <Card>
          <CardHeader><CardTitle>14.6. Meeting Logistics and Digital Workflow</CardTitle></CardHeader>
          <CardContent>
            <p>
              Agendas are scheduled in Google Calendar and include links to the relevant Drive folders. Virtual sessions are run via
              Zoom. Voting outside formal meetings occurs through an Adobe e-signed PDF that records each director's response and
              timestamp. The resulting PDF is stored alongside the meeting minutes, creating a tamper-evident audit trail.
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader><CardTitle>14.7. Transparency and Record-Keeping</CardTitle></CardHeader>
          <CardContent>
            <p>
              All committee minutes, action logs, and decision documents reside in Drive folders with read-only access for Compliance
              and Internal Audit. The platform's version history provides an immutable audit trail sufficient for internal governance
              and any future regulatory inspection.
            </p>
          </CardContent>
        </Card>
      </div>
    ),
  },

  {
    number: 15,
    title: "Conflict of Interest Standards",
    description: "Conflict Register, PAD, outside interests, gifts, rebates, monitoring and training",
    content: (
      <div className="space-y-4">
        <Card>
          <CardContent className="pt-6">
            <p>
              Conflicts of interest are inevitable in any investment business; the test of governance is how clearly those conflicts
              are identified, documented, and mitigated. The Group takes a preemptive disclosure approach. Every policy and control
              described below is executed with the firm's embedded toolset: Asana for workflow, FE Analytics for fund-level data, and
              Google Workspace for registers and documentation.
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader><CardTitle>15.1. Scope and Definitions</CardTitle></CardHeader>
          <CardContent>
            <p>
              A conflict arises when the personal, financial or other interests of a staff member, director or connected party could
              influence, or be perceived to influence, the impartial discharge of their professional obligations to clients. The
              standards in this section apply to all permanent and contract staff, Board members, and any consultant with investment
              decision input.
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader><CardTitle>15.2. The Conflict Register – A Single Source of Truth</CardTitle></CardHeader>
          <CardContent>
            <p>
              The Compliance Officer maintains a Conflict Register in Google Sheets, stored in a locked Drive folder. Each row records
              the individual's name, the nature of the conflict, mitigating controls, and review date. Staff update their own line
              items via a linked Google Form, which timestamps the submission and logs the editor's Google account, creating an
              immutable audit trail.
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader><CardTitle>15.3. Personal Account Dealing (PAD) Controls</CardTitle></CardHeader>
          <CardContent>
            <p>
              Staff may trade listed securities without pre-clearance or notification so long as their total holding remains &lt;10% of
              the readily tradable market amount (measured as the lower of free-float or 30-day average daily on-exchange volume). Any
              trade that would take the holding to 10% is prohibited without Compliance approval. Staff must keep contract notes and
              provide them on request.
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader><CardTitle>15.4. Outside Business Interests</CardTitle></CardHeader>
          <CardContent>
            <p>
              Directorships, advisory roles, and part-time occupations must be declared in the Conflict Register. The Board Chair
              reviews new declarations at each quarterly meeting and records approval or conditions in the meeting minutes. This is in
              line with AWM policy.
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader><CardTitle>15.5. Gifts and Hospitality</CardTitle></CardHeader>
          <CardContent>
            <p>
              Gifts over £100 or hospitality over £250 in value require prior approval from the Compliance Officer. Staff log offers
              via a Gifts &amp; Entertainment Google Form that populates a dedicated tab in the Conflict Register.
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader><CardTitle>15.6. Fund Rebates and Fee Sharing</CardTitle></CardHeader>
          <CardContent>
            <p>
              CBAM will not invest in any fund or share class that pays rebates, trail commission, or revenue-sharing. Only clean,
              non-rebating share classes are permitted. Pre-trade, the IA screens candidates using the FE Analytics Charges &amp; Costs
              export; any non-zero or unknown rebate field blocks approval.
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader><CardTitle>15.7. Monitoring and Escalation</CardTitle></CardHeader>
          <CardContent>
            <p>
              Compliance reviews the Conflict Register monthly. Failure to update after two reminders escalates to the COO. Serious or
              unresolved conflicts are tabled at the next Board meeting for potential disciplinary action.
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader><CardTitle>15.8. Training and Attestation</CardTitle></CardHeader>
          <CardContent>
            <p>
              All staff and directors complete an annual Conflict of Interest e-learning module, which concludes with a digital
              attestation. Completion rates are monitored in the Compliance dashboard.
            </p>
          </CardContent>
        </Card>
      </div>
    ),
  },

  {
    number: 16,
    title: "Breach Escalation Protocol",
    description: "Four-stage detection, containment, remediation and reporting ladder with severity tiers",
    content: (
      <div className="space-y-4">
        <Card>
          <CardContent className="pt-6">
            <p>
              Even the best-laid risk limits will occasionally be breached; what protects clients is how quickly and transparently the
              firm responds. Section 16 sets out a four-stage escalation ladder that can be executed with the same lean toolset that
              underpins other policies: Google Sheets dashboards, Google Forms incident tickets, Drive archives, and standard email.
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader><CardTitle>16.1. Stage 1 – Detection</CardTitle></CardHeader>
          <CardContent>
            <p>
              Risk breaches are detected either by automatic alert (e.g., a metric in RiskEngine.gsheets turning red) or manual
              observation. Automatic alerts fire an email to the SPM and Compliance Officer. Manual observations are logged via a
              Breach Ticket Google Form that timestamps the incident.
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader><CardTitle>16.2. Stage 2 – Containment</CardTitle></CardHeader>
          <CardContent>
            <p>
              Upon detection, the SPM must act within one trading day to prevent the breach from widening. For a tracking error
              overshoot, this usually involves reducing the offending active position. The containment action is written into the
              Breach Log tab of the dashboard.
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader><CardTitle>16.3. Stage 3 – Remediation</CardTitle></CardHeader>
          <CardContent>
            <p>
              After containment, the IC reviews the breach at an ad-hoc meeting scheduled within three business days. The committee
              decides on any required structural changes. Decisions are drafted live in a Google Doc. If the breach is classed as
              major, the CIO emails the Board Chair within 24 hours.
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader><CardTitle>16.4. Stage 4 – Reporting</CardTitle></CardHeader>
          <CardContent>
            <p>
              All breaches appear on a slide in the quarterly Board pack. For major breaches, the PDF remediation document is
              hyperlinked. If a breach meets the FCA's notification threshold, the Compliance Officer drafts a SUP 15.3 notice, which
              the CEO sends via the FCA Connect portal.
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader><CardTitle>16.5. Severity Definitions and Timelines</CardTitle></CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="min-w-full border border-border">
                <thead className="bg-muted">
                  <tr>
                    <th className="border border-border px-3 py-2 text-left">Severity</th>
                    <th className="border border-border px-3 py-2 text-left">Trigger</th>
                    <th className="border border-border px-3 py-2 text-left">Containment Deadline</th>
                    <th className="border border-border px-3 py-2 text-left">IC Review</th>
                    <th className="border border-border px-3 py-2 text-left">Board Notification</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td className="border border-border px-3 py-2">Minor</td>
                    <td className="border border-border px-3 py-2">0-5% over limit or one-off operational slip</td>
                    <td className="border border-border px-3 py-2">T + 4 hrs</td>
                    <td className="border border-border px-3 py-2">Next scheduled IC</td>
                    <td className="border border-border px-3 py-2">Not required</td>
                  </tr>
                  <tr>
                    <td className="border border-border px-3 py-2">Moderate</td>
                    <td className="border border-border px-3 py-2">5-10% over limit or two minors in a quarter</td>
                    <td className="border border-border px-3 py-2">T + 4 hrs</td>
                    <td className="border border-border px-3 py-2">Ad-hoc IC within 3 days</td>
                    <td className="border border-border px-3 py-2">Summary in quarterly Board pack</td>
                  </tr>
                  <tr>
                    <td className="border border-border px-3 py-2">Major</td>
                    <td className="border border-border px-3 py-2">&gt;10% over limit, liquidity breach, or three minors in a quarter</td>
                    <td className="border border-border px-3 py-2">T + 4 hrs</td>
                    <td className="border border-border px-3 py-2">Ad-hoc IC within 3 days</td>
                    <td className="border border-border px-3 py-2">CIO email within 24 hrs + Board pack</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader><CardTitle>16.6. End-to-End Audit Trail</CardTitle></CardHeader>
          <CardContent>
            <p>
              Because all stages rely on Google Forms, Sheets, and Docs, each with automatic timestamps and version history, the firm
              can recreate an end-to-end log of breach detection, decision-making, and remediation without external software.
              Compliance performs an annual sample review to verify that each step occurred within policy timelines.
            </p>
          </CardContent>
        </Card>
      </div>
    ),
  },

  {
    number: 17,
    title: "Reporting, Transparency & Record-Keeping",
    description: "Client & adviser disclosure, internal MI pack, regulator readiness, retention and ESG reporting",
    content: (
      <div className="space-y-4">
        <Card>
          <CardContent className="pt-6">
            <p>
              Transparent reporting underpins client trust and equips the Board to discharge its oversight duties. Every disclosure
              described in this section is generated with the same lean toolset used elsewhere in the policy: FE Analytics for raw
              data, Google Sheets for processing, Google Docs/Slides for narrative, and Drive for secure storage.
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader><CardTitle>17.1. Client & Adviser Disclosure</CardTitle></CardHeader>
          <CardContent>
            <p>
              The client and adviser-facing MPS Factsheet for each risk profile is updated at least weekly. FE Analytics supplies daily
              performance and risk data that feed directly into a linked Google Sheets workbook. The factsheet prominently displays
              performance against both the primary AWM Composite Benchmark and the secondary AFI &amp; ARC MPS peer benchmarks.
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader><CardTitle>17.2. Internal Management Information</CardTitle></CardHeader>
          <CardContent>
            <p>
              Board Members view a quarterly Management Information (MI) Pack stored in a restricted Drive folder. Google Slides pulls
              live charts from RiskEngine and CostEngine workbooks, ensuring the Board sees numbers that reflect the latest data. Where
              a metric breaches its limit, the slide links to a one-page remediation note drafted in Google Docs. This pack is then
              exported as a PDF at the end of the quarter.
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader><CardTitle>17.3. Regulatory Interaction Readiness</CardTitle></CardHeader>
          <CardContent>
            <p>
              Should the FCA or external auditors request evidence, Compliance will assemble the relevant PDFs and underlying
              spreadsheets within one working week. All factsheets, MI slides, and policy documents are saved as locked PDFs in Drive
              immediately after publication, preserving version history.
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader><CardTitle>17.4. Retention Schedule</CardTitle></CardHeader>
          <CardContent>
            <p>
              Client-facing documents are retained for one year. Board packs, IC minutes, and breach files remain on Drive for ten
              years. Routine working documents are preserved indefinitely unless senior staff determine they are obsolete.
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader><CardTitle>17.5. Data-Validation Check</CardTitle></CardHeader>
          <CardContent>
            <p>
              Key Google Sheets dashboards are protected with "View-only" permissions for most users. The SPM and IA hold edit rights
              and perform an informal spot-check, verifying one random metric against the FE Analytics export, each quarter.
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader><CardTitle>17.6. ESG Reporting</CardTitle></CardHeader>
          <CardContent>
            <p>
              The firm does not publish ESG or climate-impact metrics at this time. Once the ESG-labelled portfolio suite launches,
              the Board will decide whether to incorporate such disclosures into the reporting cycle.
            </p>
          </CardContent>
        </Card>
      </div>
    ),
  },

  {
    number: 18,
    title: "Review Cycle & Change-Control",
    description: "Annual review, materiality thresholds, fast-track amendments and audit trail via Asana",
    content: (
      <div className="space-y-4">
        <Card>
          <CardContent className="pt-6">
            <p>
              Policies that guide investment and governance must evolve in step with markets, regulation, and organisational
              capability. Section 18 codifies two complementary mechanisms: a calendar-anchored annual review and an exception-driven
              fast-track amendment route, managed via Asana as the firm's central task-management platform.
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader><CardTitle>18.1. Annual Comprehensive Review</CardTitle></CardHeader>
          <CardContent>
            <p>
              Every January, an Asana project named GIP Annual Review is auto-generated from a template. The project populates tasks
              for drafting, cross-checking FE Analytics dashboards, and peer review. All source material resides in a Drive folder
              linked within the relevant Asana task. When all tasks are complete, the CIO submits the draft for Board approval at the
              next scheduled meeting.
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader><CardTitle>18.2. Materiality Thresholds</CardTitle></CardHeader>
          <CardContent>
            <p>
              A change qualifies as material if it alters numeric limits, asset-class ranges, committee decision rights, or the AWM
              Composite Benchmark methodology. The Asana template contains a binary custom field, Material? Yes/No. Any task flagged
              Yes routes to a Board-approval sub-workflow.
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader><CardTitle>18.3. Fast-Track Amendment Process</CardTitle></CardHeader>
          <CardContent>
            <p>
              When regulatory shifts or market dislocations demand immediate action, the CIO creates a Fast-Track Amendment task in
              Asana. A linked Google Form captures the trigger event. The task template assigns default due dates: draft redline within
              two days, Board review within five business days.
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader><CardTitle>18.4. Communication & Implementation</CardTitle></CardHeader>
          <CardContent>
            <p>
              Compliance maintains an Asana project tagged Policy Roll-Out. When the Board approves a revision, Compliance initiates a
              Change Notice task. Sub-tasks cover intranet posting, adviser emails, and platform partner notification. Implementation
              actions, such as adjusting asset allocation weights, sit in a separate section within the same project, each with an
              owner and deadline.
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader><CardTitle>18.5. Audit Trail</CardTitle></CardHeader>
          <CardContent>
            <p>
              Asana retains an immutable history of task creation, edits, and comments. Drive version history captures every draft and
              final PDF. Compliance exports an Annual Change-Log Report each January by combining Asana's CSV export with Drive's
              version list, demonstrating end-to-end traceability.
            </p>
          </CardContent>
        </Card>
      </div>
    ),
  },

  {
    number: 19,
    title: "Definitions & Abbreviations",
    description: "Glossary of key terms used throughout the Tier 1 Group Investment Policy",
    content: (
      <div className="space-y-4">
        <Card>
          <CardContent className="pt-6">
            <p>Clarity of language is as important as clarity of process. The explanations below translate the technical shorthand used throughout this policy into plain, continuous prose.</p>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6 space-y-4">
            <p><strong>Approved Fund House List:</strong> the list of fund management companies, approved by the IC, whose multi-asset fund ranges are eligible for inclusion in the AWM Composite Benchmarks. See Section 5.2.</p>
            <p><strong>ARC MPS Benchmark:</strong> the multi-asset peer composite compiled by Asset Risk Consultants. Used as a secondary benchmark for peer group comparison only. See Section 12.3.</p>
            <p><strong>Asana:</strong> the cloud-based task-management platform used by the firm to schedule, assign, and monitor all policy-related activities. See Section 18.</p>
            <p><strong>AWM Composite Benchmark:</strong> the proprietary, rules-based, equally-weighted composite of funds from the Approved Fund House List, created in FE Analytics. Serves as the Primary Benchmark for performance and risk; its weighted-average asset allocation defines the SAA for the corresponding model portfolio. See Section 5.1.</p>
            <p><strong>Benchmark SAA:</strong> the Strategic Asset Allocation derived quarterly from the weighted-average holdings of the AWM Composite Benchmark.</p>
            <p><strong>CPI (UK Consumer Prices Index):</strong> the ONS measure of inflation against which our 'inflation-plus' return objectives are set. See Section 4.1.</p>
            <p><strong>FE Analytics:</strong> the data service supplying daily prices, OCFs, and peer benchmarks. Primary tool for constructing the AWM Composite Benchmarks and sourcing data for all quantitative dashboards.</p>
            <p><strong>Google Workspace (Forms / Sheets / Docs / Slides / Drive):</strong> the suite of cloud-based tools that constitute the backbone of internal information flow, analytics, and record-keeping.</p>
            <p><strong>Liquidity Coverage Ratio (LCR):</strong> the percentage of NAV that could be liquidated within five trading days under stress, with a minimum threshold of 1.0. See Section 10.1.</p>
            <p><strong>Primary Benchmark:</strong> the AWM Composite Benchmark designated for each model portfolio, used for all primary relative performance, risk, and attribution analysis. See Section 12.2.</p>
            <p><strong>RiskEngine.gsheets:</strong> the master workbook housing price series, risk metrics (including VaR and Tracking Error), and the colour-coded dashboard that triggers automated breach alerts. See Section 11.</p>
            <p><strong>Strategic Asset Allocation (SAA):</strong> the neutral, long-term asset class weights for a portfolio, derived directly from its Benchmark SAA. See Section 5.3.</p>
            <p><strong>Tactical Asset Allocation (TAA):</strong> shorter-term, active tilts away from the SAA, authorised by the IC within the tracking error guardrails of Sections 8 and 11.</p>
            <p><strong>Total Cost of Ownership (TCO):</strong> aggregates fund OCFs, the discretionary fee, and measured trading costs into a single basis-point figure reviewed quarterly under Section 13.</p>
            <p><strong>Tracking Error:</strong> the annualised standard deviation of the difference in returns between a portfolio and its primary benchmark. Main measure of active risk. See Section 11.2.</p>
            <p><strong>Value-at-Risk (VaR):</strong> the 95th-percentile one-year loss estimate calculated via a historical simulation engine written in Apps Script and deployed within RiskEngine.gsheets. See Section 11.1.</p>
          </CardContent>
        </Card>
      </div>
    ),
  },
];
